import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  SafeAreaView, Image,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  ScrollView,
  Alert,
  Modal,
  TextInput,
  ActivityIndicator,
  RefreshControl,
  FlatList,
  KeyboardAvoidingView,
  Platform,
  Share,
  BackHandler,
  Animated,
  Switch,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons, FontAwesome5 } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { LinearGradient } from 'expo-linear-gradient';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import axios from 'axios';

const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  // Letters
  letters:              `${API_BASE_URL}/api/letters`,
  letterById:           (id) => `${API_BASE_URL}/api/letters/${id}`,
  letterArchive:        (id) => `${API_BASE_URL}/api/letters/${id}/archive`,
  letterRestore:        (id) => `${API_BASE_URL}/api/letters/${id}/restore`,
  letterApprove:        (id) => `${API_BASE_URL}/api/letters/${id}/approve`,
  letterReminder:       (id) => `${API_BASE_URL}/api/letters/${id}/reminder`,
  letterPDF:            (id) => `${API_BASE_URL}/api/letters/${id}/pdf`,
  letterDuplicate:      (id) => `${API_BASE_URL}/api/letters/${id}/duplicate`,
  // Drafts
  drafts:               `${API_BASE_URL}/api/letters/drafts`,
  draftById:            (id) => `${API_BASE_URL}/api/letters/drafts/${id}`,
  draftSend:            (id) => `${API_BASE_URL}/api/letters/drafts/${id}/send`,
  // Letter Templates
  letterTemplates:      `${API_BASE_URL}/api/letter-templates`,
  letterTemplateById:   (id) => `${API_BASE_URL}/api/letter-templates/${id}`,
  letterTemplateUse:    (id) => `${API_BASE_URL}/api/letter-templates/${id}/use`,
  // Clients
  letterClients:        `${API_BASE_URL}/api/letter-clients`,
  letterClientById:     (id) => `${API_BASE_URL}/api/letter-clients/${id}`,
  // Analytics
  letterAnalytics:      `${API_BASE_URL}/api/letter-analytics`,
  letterDashboard:      `${API_BASE_URL}/api/letter-analytics/dashboard`,
};

const api = axios.create({
  baseURL: API_BASE_URL, timeout: 15000,
  headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' },
});
api.interceptors.request.use(async (config) => {
  try {
    const AsyncStorage = require('@react-native-async-storage/async-storage').default;
    const token = await AsyncStorage.getItem('auth_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } catch (_) {}
  return config;
});

import { useCompanyLogo } from '../../../hooks/useCompanyLogo';
const { width, height } = Dimensions.get('window');
// ─── Brand colours ────────────────────────────────────────────────────────────
const COLORS = {
  primary:   '#0e4e96',
  accent:    '#19abb5',
  gold:      '#fdb913',
  success:   '#10b981',
  warning:   '#f59e0b',
  danger:    '#dc2626',
  purple:    '#8b5cf6',
  bg:        '#f0f4f8',
  card:      '#ffffff',
  border:    '#e2e8f0',
  textDark:  '#0f172a',
  textMid:   '#475569',
  textLight: '#94a3b8',
};

// ─── Constants ────────────────────────────────────────────────────────────────
const LETTER_TYPES = [
  { id: 'business',       label: 'Business',       icon: 'briefcase-outline',       color: COLORS.primary },
  { id: 'formal',         label: 'Formal',          icon: 'file-document-outline',   color: COLORS.accent },
  { id: 'complaint',      label: 'Complaint',       icon: 'alert-circle-outline',    color: COLORS.danger },
  { id: 'inquiry',        label: 'Inquiry',         icon: 'help-circle-outline',     color: COLORS.purple },
  { id: 'recommendation', label: 'Recommendation',  icon: 'star-outline',            color: COLORS.gold },
  { id: 'tender',         label: 'Tender',          icon: 'gavel',                   color: '#06b6d4' },
  { id: 'resignation',    label: 'Resignation',     icon: 'exit-to-app',             color: COLORS.warning },
  { id: 'appointment',    label: 'Appointment',     icon: 'account-check-outline',   color: COLORS.success },
  { id: 'termination',    label: 'Termination',     icon: 'close-circle-outline',    color: '#f97316' },
  { id: 'invitation',     label: 'Invitation',      icon: 'email-heart-outline',     color: '#ec4899' },
];

const SALUTATIONS    = ['Dear Sir/Madam','Dear Mr.','Dear Mrs.','Dear Ms.','Dear Dr.','Dear Prof.','To Whom It May Concern','Dear Team','Dear Colleagues'];
const CLOSINGS       = ['Yours sincerely','Yours faithfully','Kind regards','Best regards','Respectfully yours','Warm regards','With best wishes','Sincerely','Regards'];
const FONT_FAMILIES  = ['Times New Roman','Arial','Calibri','Georgia','Helvetica','Garamond'];
const PRIORITIES     = [
  { id: 'urgent',  label: 'Urgent',  color: COLORS.danger  },
  { id: 'high',    label: 'High',    color: COLORS.warning },
  { id: 'normal',  label: 'Normal',  color: COLORS.success },
  { id: 'low',     label: 'Low',     color: COLORS.textLight },
];

// ─── Sample data helpers ──────────────────────────────────────────────────────
const SAMPLE_LETTERS = [
  {
    id: 'ltr_001',
    letterNumber: 'LTR-2024-001',
    letterType: 'business',
    subject: 'Project Proposal Submission – Office Renovation',
    salutation: 'Dear Mr. Smith',
    body: 'I am writing to formally submit our proposal for the office renovation project as discussed in our recent meeting.\n\nOur comprehensive proposal includes detailed specifications, timeline, and competitive pricing for all aspects of the renovation work.\n\nKey highlights:\n• Complete electrical system upgrade\n• Modern HVAC installation\n• Interior design and finishing\n• 2-year warranty on all work',
    closing: 'Yours sincerely',
    signature: 'James Walker',
    date: '2024-11-15',
    reference: 'REF/ABC/2024/001',
    priority: 'high',
    status: 'sent',
    approvalStatus: 'approved',
    approvedBy: 'Director',
    approvalDate: '2024-11-14',
    tags: ['proposal', 'renovation', 'construction'],
    enclosures: ['Project Proposal', 'Company Credentials'],
    copies: ['info@company.com'],
    viewCount: 12,
    downloadCount: 4,
    createdAt: '2024-11-15T10:30:00Z',
    sentAt: '2024-11-15T14:20:00Z',
    followUpDate: '2024-11-29',
    recipient: { name: 'John Smith', title: 'Project Manager', company: 'ABC Construction Ltd', address: '123 Business Street, London, SW1A 1AA', email: 'john@abc.com', phone: '+44 20 1234 5678' },
    sender: { name: 'James Walker', title: 'Managing Director', company: 'JobEng Ltd', address: '10 Engineering Way, London', phone: '+44 20 9876 5432', email: 'james@jobeng.ai' },
    letterhead: { enabled: true, companyName: 'JobEng Ltd', address: '10 Engineering Way, London, EC1A 1BB', phone: '+44 20 9876 5432', email: 'info@jobeng.ai', website: 'www.jobeng.ai' },
    versions: [
      { version: '1.0', date: '2024-11-15', author: 'James Walker', note: 'Initial draft' },
    ],
    reminders: [],
    comments: [{ id: 'c1', author: 'Director', text: 'Good proposal, approved.', date: '2024-11-14T09:00:00Z' }],
    formatting: { fontSize: 12, fontFamily: 'Times New Roman', lineSpacing: 1.5, margins: { top: 2.5, bottom: 2.5, left: 2.5, right: 2.5 } },
  },
  {
    id: 'ltr_002',
    letterNumber: 'LTR-2024-002',
    letterType: 'inquiry',
    subject: 'Partnership Opportunity – Solar Installation Services',
    salutation: 'Dear Ms. Johnson',
    body: 'I hope this letter finds you well. I am writing to explore potential partnership opportunities between our companies in the renewable energy sector.\n\nYour Company has established itself as a leading provider of solar installation services, and we believe there could be significant mutual benefits in collaborating on upcoming projects.',
    closing: 'Kind regards',
    signature: 'James Walker',
    date: '2024-11-12',
    reference: 'REF/GES/2024/002',
    priority: 'normal',
    status: 'sent',
    approvalStatus: 'approved',
    approvedBy: 'Manager',
    approvalDate: '2024-11-12',
    tags: ['partnership', 'solar', 'inquiry'],
    enclosures: ['Company Profile', 'Project Portfolio'],
    copies: [],
    viewCount: 8,
    downloadCount: 2,
    createdAt: '2024-11-12T09:15:00Z',
    sentAt: '2024-11-12T16:45:00Z',
    followUpDate: null,
    recipient: { name: 'Sarah Johnson', title: 'Procurement Manager', company: 'Green Energy Solutions', address: '456 Eco Drive, Manchester, M1 2AB', email: 'sarah@green.com', phone: '+44 161 234 5678' },
    sender: { name: 'James Walker', title: 'Managing Director', company: 'JobEng Ltd', address: '10 Engineering Way, London', phone: '+44 20 9876 5432', email: 'james@jobeng.ai' },
    letterhead: { enabled: true, companyName: 'JobEng Ltd', address: '10 Engineering Way, London, EC1A 1BB', phone: '+44 20 9876 5432', email: 'info@jobeng.ai', website: 'www.jobeng.ai' },
    versions: [{ version: '1.0', date: '2024-11-12', author: 'James Walker', note: 'Initial' }],
    reminders: [],
    comments: [],
    formatting: { fontSize: 12, fontFamily: 'Times New Roman', lineSpacing: 1.5, margins: { top: 2.5, bottom: 2.5, left: 2.5, right: 2.5 } },
  },
  {
    id: 'ltr_003',
    letterNumber: 'LTR-2024-003',
    letterType: 'tender',
    subject: 'Tender Submission – Public Building Maintenance Contract',
    salutation: 'Dear Procurement Team',
    body: 'We are pleased to submit our tender for the Public Building Maintenance Contract (Reference: PBC/2024/001).\n\nOur company has extensive experience in public sector maintenance services, having successfully completed similar contracts over the past decade.',
    closing: 'Respectfully yours',
    signature: 'James Walker',
    date: '2024-11-10',
    reference: 'REF/CC/PBC/2024/001',
    priority: 'urgent',
    status: 'sent',
    approvalStatus: 'approved',
    approvedBy: 'Director',
    approvalDate: '2024-11-09',
    tags: ['tender', 'public', 'maintenance'],
    enclosures: ['Tender Documents', 'Insurance Certificates'],
    copies: [],
    viewCount: 23,
    downloadCount: 7,
    createdAt: '2024-11-10T08:00:00Z',
    sentAt: '2024-11-10T17:30:00Z',
    followUpDate: '2024-12-01',
    recipient: { name: 'Procurement Team', title: '', company: 'City Council', address: 'Town Hall, City Centre, CC1 1AA', email: 'procurement@city.gov.uk', phone: '+44 20 5555 1234' },
    sender: { name: 'James Walker', title: 'Director', company: 'JobEng Ltd', address: '10 Engineering Way, London', phone: '+44 20 9876 5432', email: 'james@jobeng.ai' },
    letterhead: { enabled: true, companyName: 'JobEng Ltd', address: '10 Engineering Way, London, EC1A 1BB', phone: '+44 20 9876 5432', email: 'info@jobeng.ai', website: 'www.jobeng.ai' },
    versions: [
      { version: '2.0', date: '2024-11-10', author: 'James Walker', note: 'Final version' },
      { version: '1.0', date: '2024-11-08', author: 'James Walker', note: 'Initial draft' },
    ],
    reminders: [{ id: 'r1', date: '2024-12-01', note: 'Follow up on tender decision' }],
    comments: [],
    formatting: { fontSize: 12, fontFamily: 'Times New Roman', lineSpacing: 1.5, margins: { top: 2.5, bottom: 2.5, left: 2.5, right: 2.5 } },
  },
];

const SAMPLE_DRAFTS = [
  {
    id: 'draft_001',
    letterNumber: 'DRAFT-001',
    letterType: 'formal',
    subject: 'Follow-up on Meeting Discussion',
    salutation: 'Dear Mr. Wilson',
    body: 'I hope this letter finds you well. Following our recent meeting regarding the infrastructure project...',
    closing: 'Best regards',
    signature: 'James Walker',
    date: '2024-11-20',
    reference: '',
    priority: 'normal',
    status: 'draft',
    approvalStatus: 'pending',
    tags: ['follow-up', 'meeting'],
    enclosures: [],
    copies: [],
    viewCount: 0,
    downloadCount: 0,
    createdAt: '2024-11-20T08:00:00Z',
    lastModified: '2024-11-20T10:30:00Z',
    recipient: { name: 'Mike Wilson', title: 'Operations Director', company: 'Modern Interiors', address: '789 Design Ave, Birmingham', email: 'mike@modern.com', phone: '' },
    sender: { name: 'James Walker', title: 'Managing Director', company: 'JobEng Ltd', address: '10 Engineering Way, London', phone: '+44 20 9876 5432', email: 'james@jobeng.ai' },
    letterhead: { enabled: true, companyName: 'JobEng Ltd', address: '10 Engineering Way, London, EC1A 1BB', phone: '+44 20 9876 5432', email: 'info@jobeng.ai', website: 'www.jobeng.ai' },
    versions: [{ version: '1.0', date: '2024-11-20', author: 'James Walker', note: 'Draft' }],
    reminders: [],
    comments: [],
    formatting: { fontSize: 12, fontFamily: 'Times New Roman', lineSpacing: 1.5, margins: { top: 2.5, bottom: 2.5, left: 2.5, right: 2.5 } },
  },
];

const SAMPLE_TEMPLATES = [
  {
    id: 'tpl_001',
    name: 'Business Proposal',
    category: 'business',
    letterType: 'business',
    subject: 'Business Proposal – {{project_name}}',
    salutation: 'Dear {{recipient_name}}',
    body: 'I am writing to present our proposal for {{project_name}}.\n\nOur comprehensive proposal addresses all your requirements and includes:\n• {{requirement_1}}\n• {{requirement_2}}\n• {{requirement_3}}\n\nWe would welcome the opportunity to discuss our proposal in detail.\n\nThank you for considering our proposal.',
    closing: 'Yours sincerely',
    variables: ['project_name', 'recipient_name', 'requirement_1', 'requirement_2', 'requirement_3'],
    usageCount: 24,
    isFavourite: true,
    createdAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'tpl_002',
    name: 'Formal Complaint',
    category: 'complaint',
    letterType: 'complaint',
    subject: 'Formal Complaint – {{issue_description}}',
    salutation: 'Dear Sir/Madam',
    body: 'I am writing to formally register a complaint regarding {{issue_description}} that occurred on {{incident_date}}.\n\nDetails:\n• Date: {{incident_date}}\n• Nature of problem: {{problem_description}}\n• Impact: {{impact_description}}\n\nI am seeking the following resolution:\n• {{resolution_1}}\n• {{resolution_2}}\n\nI trust you will investigate this matter promptly.',
    closing: 'Yours faithfully',
    variables: ['issue_description', 'incident_date', 'problem_description', 'impact_description', 'resolution_1', 'resolution_2'],
    usageCount: 11,
    isFavourite: false,
    createdAt: '2024-01-02T00:00:00Z',
  },
  {
    id: 'tpl_003',
    name: 'Tender Submission',
    category: 'tender',
    letterType: 'tender',
    subject: 'Tender Submission – {{contract_name}}',
    salutation: 'Dear Procurement Team',
    body: 'We are pleased to submit our tender for {{contract_name}} (Reference: {{tender_ref}}).\n\nOur submission includes:\n• Comprehensive service proposal\n• Detailed pricing schedule\n• Health and safety procedures\n• Quality assurance protocols\n• Insurance documentation\n\nWe are confident our proposal offers excellent value. We look forward to your consideration.',
    closing: 'Respectfully yours',
    variables: ['contract_name', 'tender_ref'],
    usageCount: 18,
    isFavourite: true,
    createdAt: '2024-01-03T00:00:00Z',
  },
  {
    id: 'tpl_004',
    name: 'Recommendation Letter',
    category: 'recommendation',
    letterType: 'recommendation',
    subject: 'Letter of Recommendation for {{person_name}}',
    salutation: 'To Whom It May Concern',
    body: 'I am writing to provide a strong recommendation for {{person_name}}, who {{relationship_description}}.\n\nDuring this time, {{person_name}} consistently demonstrated:\n• {{quality_1}}\n• {{quality_2}}\n• {{quality_3}}\n\nI recommend {{person_name}} without reservation.',
    closing: 'Sincerely',
    variables: ['person_name', 'relationship_description', 'quality_1', 'quality_2', 'quality_3'],
    usageCount: 8,
    isFavourite: false,
    createdAt: '2024-01-04T00:00:00Z',
  },
  {
    id: 'tpl_005',
    name: 'Meeting Request',
    category: 'business',
    letterType: 'business',
    subject: 'Meeting Request – {{meeting_purpose}}',
    salutation: 'Dear {{recipient_name}}',
    body: 'I am writing to request a meeting to discuss {{meeting_purpose}}.\n\nProposed details:\n• Date: {{proposed_date}}\n• Time: {{proposed_time}}\n• Duration: {{duration}}\n• Location: {{location}}\n\nI am flexible and can accommodate your schedule.\n\nThank you for considering this request.',
    closing: 'Best regards',
    variables: ['meeting_purpose', 'recipient_name', 'proposed_date', 'proposed_time', 'duration', 'location'],
    usageCount: 31,
    isFavourite: true,
    createdAt: '2024-01-05T00:00:00Z',
  },
  {
    id: 'tpl_006',
    name: 'Appointment Letter',
    category: 'hr',
    letterType: 'appointment',
    subject: 'Appointment Letter – {{position_title}}',
    salutation: 'Dear {{candidate_name}}',
    body: 'We are pleased to offer you the position of {{position_title}} at {{company_name}}, effective {{start_date}}.\n\nTerms of employment:\n• Salary: {{salary}}\n• Working hours: {{hours}}\n• Probation period: {{probation}}\n• Reporting to: {{reporting_manager}}\n\nPlease confirm your acceptance by signing and returning the enclosed copy.\n\nWe look forward to welcoming you to the team.',
    closing: 'Yours sincerely',
    variables: ['candidate_name', 'position_title', 'company_name', 'start_date', 'salary', 'hours', 'probation', 'reporting_manager'],
    usageCount: 15,
    isFavourite: false,
    createdAt: '2024-01-06T00:00:00Z',
  },
];

const SAMPLE_CLIENTS = [
  { id: 'cl_1', name: 'John Smith', title: 'Project Manager', company: 'ABC Construction Ltd', address: '123 Business Street, London', email: 'john@abc.com', phone: '+44 20 1234 5678' },
  { id: 'cl_2', name: 'Sarah Johnson', title: 'Procurement Manager', company: 'Green Energy Solutions', address: '456 Eco Drive, Manchester', email: 'sarah@green.com', phone: '+44 161 234 5678' },
  { id: 'cl_3', name: 'Mike Wilson', title: 'Operations Director', company: 'Modern Interiors', address: '789 Design Ave, Birmingham', email: 'mike@modern.com', phone: '+44 121 345 6789' },
  { id: 'cl_4', name: 'Emma Davies', title: 'HR Manager', company: 'TechCorp UK', address: '1 Tech Park, Bristol', email: 'emma@techcorp.co.uk', phone: '+44 117 456 7890' },
];

// ─── LetterTemplateCard (React.memo) ─────────────────────────────────────────
const LetterTemplateCard = React.memo(({ item, onPress }) => {
  const typeInfo = LETTER_TYPES.find(t => t.id === item.letterType) || LETTER_TYPES[0];
  return (
    <TouchableOpacity style={styles.tplCard} onPress={() => onPress(item)}>
      <View style={[styles.tplIcon, { backgroundColor: `${typeInfo.color}18` }]}>
        <MaterialCommunityIcons name={typeInfo.icon} size={24} color={typeInfo.color} />
      </View>
      <View style={styles.tplInfo}>
        <View style={styles.tplTitleRow}>
          <Text style={styles.tplName}>{item.name}</Text>
          {item.isFavourite && <MaterialCommunityIcons name="star" size={14} color="#fdb913" />}
        </View>
        <Text style={styles.tplSubject} numberOfLines={1}>{item.subject}</Text>
        <View style={styles.tplFooter}>
          <Text style={styles.tplUsage}>Used {item.usageCount||0}×</Text>
          {item.variables?.length > 0 && <Text style={styles.tplVars}>{item.variables.length} variables</Text>}
        </View>
      </View>
      <Ionicons name="chevron-forward" size={20} color="#94a3b8" />
    </TouchableOpacity>
  );
});

// ─── LetterCard (React.memo) ──────────────────────────────────────────────────
const LetterCard = React.memo(({ item, bulkMode, bulkSelected, onPress, onLongPress }) => {
  const typeInfo  = LETTER_TYPES.find(t => t.id === item.letterType) || LETTER_TYPES[0];
  const isBulkSel = bulkSelected.includes(item.id);
  const getStatusColor = (status) => ({ sent: '#10b981', draft: '#f59e0b', archived: '#94a3b8', pending: '#8b5cf6' }[status] || '#94a3b8');
  const getPriorityColor = (p) => ({ high: '#dc2626', urgent: '#7c3aed', normal: '#0e4e96', low: '#94a3b8' }[p] || '#0e4e96');
  return (
    <TouchableOpacity
      style={[styles.card, isBulkSel && styles.cardSelected]}
      onPress={() => onPress(item)}
      onLongPress={() => onLongPress(item)}
      activeOpacity={0.8}
    >
      {bulkMode && (
        <View style={[styles.bulkCheck, isBulkSel && styles.bulkCheckSelected]}>
          {isBulkSel && <Ionicons name="checkmark" size={14} color="#fff" />}
        </View>
      )}
      <View style={styles.cardHeader}>
        <View style={[styles.typeIcon, { backgroundColor: `${typeInfo.color}18` }]}>
          <MaterialCommunityIcons name={typeInfo.icon} size={22} color={typeInfo.color} />
        </View>
        <View style={styles.cardHeaderText}>
          <Text style={styles.cardSubject} numberOfLines={1}>{item.subject || '(No subject)'}</Text>
          <Text style={styles.cardRecipient} numberOfLines={1}>
            {item.recipient?.name}{item.recipient?.company ? ` · ${item.recipient.company}` : ''}
          </Text>
        </View>
        <View style={styles.cardMeta}>
          <View style={[{ paddingHorizontal:6, paddingVertical:2, borderRadius:4, backgroundColor: getPriorityColor(item.priority)+'18' }]}>
            <Text style={[{ fontSize:10, color: getPriorityColor(item.priority), fontWeight:'600' }]}>{item.priority}</Text>
          </View>
          <Text style={styles.cardDate}>{new Date(item.createdAt).toLocaleDateString()}</Text>
        </View>
      </View>
      <Text style={styles.cardPreview} numberOfLines={2}>{item.body}</Text>
      {item.tags?.length > 0 && (
        <View style={{ flexDirection:'row', flexWrap:'wrap', gap:4, marginTop:4 }}>
          {item.tags.slice(0,4).map((tag,i) => (
            <View key={i} style={styles.tagChip}><Text style={styles.tagText}>#{tag}</Text></View>
          ))}
        </View>
      )}
      <View style={styles.cardFooter}>
        <Text style={styles.cardRef}>{item.letterNumber}</Text>
        <View style={{ flexDirection:'row', gap:8, alignItems:'center' }}>
          <View style={styles.cardStat}><MaterialCommunityIcons name="eye-outline" size={12} color="#94a3b8" /><Text style={styles.cardStatText}>{item.viewCount||0}</Text></View>
          <View style={styles.cardStat}><MaterialCommunityIcons name="download-outline" size={12} color="#94a3b8" /><Text style={styles.cardStatText}>{item.downloadCount||0}</Text></View>
          <View style={[styles.statusDot, { backgroundColor: getStatusColor(item.status) }]} />
        </View>
      </View>
      {item.approvalStatus === 'pending' && (
        <View style={styles.pendingBanner}>
          <MaterialCommunityIcons name="clock-alert-outline" size={14} color="#f59e0b" />
          <Text style={styles.pendingText}>Awaiting Approval</Text>
        </View>
      )}
    </TouchableOpacity>
  );
});

// ─── Default letter form ──────────────────────────────────────────────────────
const defaultLetterForm = () => ({
  recipient: { name: '', title: '', company: '', address: '', email: '', phone: '' },
  sender: { name: 'James Walker', title: 'Managing Director', company: 'JobEng Ltd', address: '10 Engineering Way, London, EC1A 1BB', phone: '+44 20 9876 5432', email: 'james@jobeng.ai' },
  letterType: 'business',
  subject: '',
  salutation: 'Dear Sir/Madam',
  body: '',
  closing: 'Yours sincerely',
  signature: 'James Walker',
  date: new Date().toISOString().split('T')[0],
  reference: '',
  priority: 'normal',
  tags: [],
  enclosures: [],
  copies: [],
  letterhead: {
    enabled: true,
    companyName: 'JobEng Ltd',
    address: '10 Engineering Way, London, EC1A 1BB',
    phone: '+44 20 9876 5432',
    email: 'info@jobeng.ai',
    website: 'www.jobeng.ai',
  },
  formatting: {
    fontSize: 12,
    fontFamily: 'Times New Roman',
    lineSpacing: 1.5,
    margins: { top: 2.5, bottom: 2.5, left: 2.5, right: 2.5 },
  },
  reminders: [],
  comments: [],
  versions: [],
});

// ─── AI Service ───────────────────────────────────────────────────────────────
const CLAUDE_API_KEY = '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';

const generateAILetter = async (prompt, letterType, recipientName, subject) => {
  // Wrap fetch + json() in ONE promise so the timeout covers the entire operation.
  // Promise.race only around fetch() leaves response.json() unguarded — it can still hang.
  const aiCallPromise = (async () => {
    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-api-key': CLAUDE_API_KEY, 'anthropic-version': '2023-06-01' },
      body: JSON.stringify({
        model: 'claude-sonnet-4-20250514',
        max_tokens: 1000,
        messages: [{
          role: 'user',
          content: `Write a professional ${letterType} letter body (body text only, no salutation/closing) for: ${subject || prompt}. Recipient: ${recipientName || 'the recipient'}. Additional context: ${prompt}. Use formal UK English, 3-5 paragraphs, professional tone.`,
        }],
      }),
    });
    if (!response.ok) throw new Error(`API ${response.status}`);
    const data = await response.json();
    if (!data.content?.[0]?.text) throw new Error('Empty response');
    return data.content[0].text;
  })();
  const timeoutPromise = new Promise((_, reject) =>
    setTimeout(() => reject(new Error('AI request timed out')), 15000)
  );
  try {
    return await Promise.race([aiCallPromise, timeoutPromise]);
  } catch (e) {
    console.log('AI error, using fallback:', e?.message);
  }
  // Fallback
  const fallbacks = {
    business:       `I am writing to you regarding ${subject || 'the matter at hand'}.\n\nWe have carefully reviewed the requirements and are pleased to present our comprehensive approach. Our team has extensive experience in this field and is committed to delivering exceptional results.\n\nWe look forward to discussing this further at your convenience.`,
    complaint:      `I am writing to formally register my concern regarding ${subject || 'the issue described'}.\n\nDespite previous attempts to resolve this matter, the situation remains unsatisfactory. I would appreciate your urgent attention to this issue.\n\nI expect a response within 14 working days.`,
    inquiry:        `I hope this letter finds you well. I am writing to inquire about ${subject || 'your services'}.\n\nWe are currently exploring options in this area and believe your organisation may be well-placed to assist us. Could you please provide further information at your earliest convenience?\n\nI look forward to your response.`,
    recommendation: `It is with great pleasure that I write to provide this recommendation.\n\nDuring our time working together, the individual demonstrated exceptional professionalism and commitment. Their contributions were significant and consistently exceeded expectations.\n\nI have no hesitation in recommending them for this opportunity.`,
    tender:         `We are pleased to submit our tender in response to your recent invitation.\n\nOur organisation has considerable experience in delivering similar contracts and we are confident in our ability to meet your requirements to the highest standard.\n\nWe welcome the opportunity to discuss our proposal further.`,
  };
  return fallbacks[letterType] || fallbacks.business;
};

// ─── PDF Generator ────────────────────────────────────────────────────────────
// Pre-fetch logo as base64 so the print renderer never makes network requests (which hang)
// FileReader & blob() are browser APIs — they do NOT exist in React Native.
// Use fetch() with arrayBuffer() + manual base64 encoding instead.
const fetchLogoBase64 = async (url) => {
  if (!url) return null;
  try {
    const fetchPromise2 = fetch(url);
    const timeoutPromise2 = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('logo fetch timeout')), 8000)
    );
    const resp = await Promise.race([fetchPromise2, timeoutPromise2]);
    if (!resp.ok) return null;
    const arrayBuffer = await resp.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    // Manual base64 encode — btoa() works in RN's Hermes engine
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
    const b64 = btoa(binary);
    const contentType = resp.headers.get('content-type') || 'image/png';
    return `data:${contentType};base64,${b64}`;
  } catch (_) {
    return null; // network error or timeout — print without logo
  }
};

const buildLetterHTML = (letter, logoBase64 = null) => {
  const fmt = letter.formatting || {};
  const lh  = letter.letterhead  || {};
  return `<!DOCTYPE html><html><head><meta charset="utf-8">
  <style>
    @page { size:A4; margin:${fmt.margins?.top||2.5}cm ${fmt.margins?.right||2.5}cm ${fmt.margins?.bottom||2.5}cm ${fmt.margins?.left||2.5}cm; }
    body { font-family:'Times New Roman',serif; font-size:${fmt.fontSize||12}pt; line-height:${fmt.lineSpacing||1.5}; color:#000; margin:0; padding:0; }
    .lh { text-align:center; border-bottom:2px solid #0e4e96; padding-bottom:18px; margin-bottom:28px; }
    .lh-logo { height:50px; margin-bottom:6px; object-fit:contain; display:block; margin-left:auto; margin-right:auto; }
    .lh-name { font-size:22pt; font-weight:bold; color:#0e4e96; margin-bottom:6px; }
    .lh-detail { font-size:9pt; color:#555; }
    .date-ref { display:flex; justify-content:space-between; margin-bottom:24px; font-size:11pt; }
    .recip { margin-bottom:24px; }
    .recip-name { font-weight:bold; }
    .subject { font-weight:bold; text-align:center; margin-bottom:20px; text-decoration:underline; }
    .body { white-space:pre-line; text-align:justify; margin-bottom:28px; }
    .closing { margin-bottom:60px; }
    .sig-line { border-bottom:1px solid #333; width:180px; margin-bottom:4px; }
    .enc { margin-top:24px; font-size:10pt; }
    .footer { text-align:center; font-size:8pt; color:#888; border-top:1px solid #ddd; padding-top:8px; margin-top:40px; }
  </style></head><body>
  ${lh.enabled ? `<div class="lh">${logoBase64 ? `<img src="${logoBase64}" class="lh-logo"/><br/>` : ''}<div class="lh-name">${lh.companyName||''}</div><div class="lh-detail">${lh.address||''}<br>Tel: ${lh.phone||''} | Email: ${lh.email||''}${lh.website?`<br>${lh.website}`:''}</div></div>` : ''}
  <div class="date-ref"><span>Date: ${new Date(letter.date).toLocaleDateString('en-GB',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}</span>${letter.reference?`<span>Ref: ${letter.reference}</span>`:''}</div>
  <div class="recip"><div class="recip-name">${letter.recipient?.name||''}</div>${letter.recipient?.title?`<div>${letter.recipient.title}</div>`:''} ${letter.recipient?.company?`<div>${letter.recipient.company}</div>`:''}<div>${letter.recipient?.address||''}</div></div>
  ${letter.subject?`<div class="subject">Re: ${letter.subject}</div>`:''}
  <div>${letter.salutation},</div><br>
  <div class="body">${letter.body||''}</div>
  <div class="closing">${letter.closing},</div>
  <div><div class="sig-line"></div><div>${letter.signature||''}</div>${letter.sender?.title?`<div>${letter.sender.title}</div>`:''}</div>
  ${letter.enclosures?.length?`<div class="enc"><strong>Enclosures:</strong><br>${letter.enclosures.map(e=>`• ${e}`).join('<br>')}</div>`:''}
  ${letter.copies?.length?`<div class="enc"><strong>Copies to:</strong><br>${letter.copies.map(c=>`• ${c}`).join('<br>')}</div>`:''}
  <div class="footer">Letter ${letter.letterNumber||''} | Generated ${new Date().toLocaleDateString()}</div>
  </body></html>`;
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function LettersManagementScreen({ navigation, route }) {
  const { logo: companyLogo, companyName, initials: companyInitials } = useCompanyLogo();

  const { client, mode = 'list' } = route?.params || {};

  // ── Core state ──
  const [isLoading,    setIsLoading]    = useState(true);
  const [refreshing,   setRefreshing]   = useState(false);
  const [activeTab,    setActiveTab]    = useState('active');
  const [searchQuery,  setSearchQuery]  = useState('');
  const [sortBy,       setSortBy]       = useState('newest');

  // ── Data state ──
  const [letters,    setLetters]    = useState([]);
  const [drafts,     setDrafts]     = useState([]);
  const [archived,   setArchived]   = useState([]);
  const [templates,  setTemplates]  = useState([]);
  const [clients,    setClients]    = useState([]);

  // ── UI flags ──
  const [showCompose,       setShowCompose]       = useState(false);
  const [showDetail,        setShowDetail]        = useState(false);
  const [showTemplate,      setShowTemplate]      = useState(false);
  const [showFilter,        setShowFilter]        = useState(false);
  const [showVersions,      setShowVersions]      = useState(false);
  const [showFormatting,    setShowFormatting]    = useState(false);
  const [showAnalytics,     setShowAnalytics]     = useState(false);
  const [showClientPicker,  setShowClientPicker]  = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showAddComment,    setShowAddComment]    = useState(false);
  const [showVarFill,       setShowVarFill]       = useState(false);
  const [selectedTemplate,  setSelectedTemplate]  = useState(null);
  const [varValues,         setVarValues]         = useState({});

  // ── Selected / editing ──
  const [selectedLetter,   setSelectedLetter]   = useState(null);
  const [letterForm,       setLetterForm]       = useState(defaultLetterForm());

  // Sync company profile into letter form when hook data loads
  useEffect(() => {
    if (companyName || companyLogo) {
      setLetterForm(prev => ({
        ...prev,
        sender: { ...prev.sender, company: companyName || prev.sender.company },
        letterhead: {
          ...prev.letterhead,
          companyName: companyName || prev.letterhead.companyName,
          logo: companyLogo || prev.letterhead.logo || null,
        },
      }));
    }
  }, [companyName, companyLogo]);
  const [composeMode,      setComposeMode]      = useState('new'); // 'new' | 'edit' | 'reply' | 'forward'

  // ── In-progress flags ──
  const [isSending,    setIsSending]    = useState(false);
  const [isGenAI,      setIsGenAI]      = useState(false);
  const [isGenPDF,     setIsGenPDF]     = useState(false);

  // ── Filters ──
  const [filters, setFilters] = useState({ type: 'all', priority: 'all', approvalStatus: 'all', dateRange: 'all' });

  // ── Misc ──
  const [aiPrompt,       setAiPrompt]       = useState('');
  const [showAiPrompt,   setShowAiPrompt]   = useState(false);
  const [newComment,     setNewComment]     = useState('');
  const [newReminder,    setNewReminder]    = useState({ date: '', note: '' });
  const [bulkSelected,   setBulkSelected]   = useState([]);
  const [bulkMode,       setBulkMode]       = useState(false);
  const [newTag,         setNewTag]         = useState('');
  const [newEnclosure,   setNewEnclosure]   = useState('');
  const [newCopy,        setNewCopy]        = useState('');

  const fadeAnim = useRef(new Animated.Value(0)).current;

  // ── Init ──
  useEffect(() => {
    init();
    const backHandler = BackHandler.addEventListener('hardwareBackPress', () => {
      if (showCompose || showDetail || showTemplate || showFilter) {
        setShowCompose(false); setShowDetail(false); setShowTemplate(false); setShowFilter(false);
        return true;
      }
      return false;
    });
    return () => backHandler.remove();
  }, []);

  useEffect(() => {
    if (mode === 'create' && client) {
      const form = defaultLetterForm();
      form.recipient = { name: client.name||'', title: client.title||'', company: client.company||'', address: client.address||'', email: client.email||'', phone: client.phone||'' };
      setLetterForm(form);
      setComposeMode('new');
      setShowCompose(true);
    }
  }, [mode, client]);

  useEffect(() => {
    Animated.timing(fadeAnim, { toValue: 1, duration: 400, useNativeDriver: true }).start();
  }, [activeTab]);

  const init = async () => {
    setIsLoading(true);
    try {
      // ── Serve from cache immediately ──
      const [lRaw, dRaw, aRaw, tRaw, cRaw] = await Promise.all([
        AsyncStorage.getItem('letters_v2'),
        AsyncStorage.getItem('letter_drafts_v2'),
        AsyncStorage.getItem('letter_archived_v2'),
        AsyncStorage.getItem('letter_templates_v2'),
        AsyncStorage.getItem('letter_clients_v2'),
      ]);
      setLetters(lRaw ? JSON.parse(lRaw) : SAMPLE_LETTERS);
      setDrafts(dRaw   ? JSON.parse(dRaw)   : SAMPLE_DRAFTS);
      setArchived(aRaw ? JSON.parse(aRaw)   : []);
      setTemplates(tRaw ? JSON.parse(tRaw)  : SAMPLE_TEMPLATES);
      setClients(cRaw  ? JSON.parse(cRaw)   : SAMPLE_CLIENTS);
      if (!lRaw) await AsyncStorage.setItem('letters_v2',          JSON.stringify(SAMPLE_LETTERS));
      if (!dRaw) await AsyncStorage.setItem('letter_drafts_v2',    JSON.stringify(SAMPLE_DRAFTS));
      if (!tRaw) await AsyncStorage.setItem('letter_templates_v2', JSON.stringify(SAMPLE_TEMPLATES));
      if (!cRaw) await AsyncStorage.setItem('letter_clients_v2',   JSON.stringify(SAMPLE_CLIENTS));
      // ── Refresh from API in background ──
      try {
        const [lRes, dRes, tRes, cRes] = await Promise.all([
          api.get(API_ENDPOINTS.letters),
          api.get(API_ENDPOINTS.drafts),
          api.get(API_ENDPOINTS.letterTemplates),
          api.get(API_ENDPOINTS.letterClients),
        ]);
        if (lRes.data?.success && Array.isArray(lRes.data.data)) { setLetters(lRes.data.data); await AsyncStorage.setItem('letters_v2', JSON.stringify(lRes.data.data)); }
        if (dRes.data?.success && Array.isArray(dRes.data.data)) { setDrafts(dRes.data.data);   await AsyncStorage.setItem('letter_drafts_v2', JSON.stringify(dRes.data.data)); }
        if (tRes.data?.success && Array.isArray(tRes.data.data)) { setTemplates(tRes.data.data); await AsyncStorage.setItem('letter_templates_v2', JSON.stringify(tRes.data.data)); }
        if (cRes.data?.success && Array.isArray(cRes.data.data)) { setClients(cRes.data.data);   await AsyncStorage.setItem('letter_clients_v2', JSON.stringify(cRes.data.data)); }
      } catch (_) { /* network unavailable — cache already served */ }
    } catch (e) {
      console.error(e);
      setLetters(SAMPLE_LETTERS); setDrafts(SAMPLE_DRAFTS); setTemplates(SAMPLE_TEMPLATES); setClients(SAMPLE_CLIENTS);
    }
    setIsLoading(false);
  };

  const saveAll = async (l, d, a) => {
    const ll = l !== undefined ? l : letters;
    const dd = d !== undefined ? d : drafts;
    const aa = a !== undefined ? a : archived;
    await Promise.all([
      AsyncStorage.setItem('letters_v2',         JSON.stringify(ll)),
      AsyncStorage.setItem('letter_drafts_v2',   JSON.stringify(dd)),
      AsyncStorage.setItem('letter_archived_v2', JSON.stringify(aa)),
    ]);
  };

  // ── Helpers ──
  const genLetterNumber = () => {
    const now = new Date();
    return `LTR-${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}-${Math.floor(Math.random()*900+100)}`;
  };

  const getTypeInfo = (id) => LETTER_TYPES.find(t => t.id === id) || LETTER_TYPES[0];
  const getPriorityInfo = (id) => PRIORITIES.find(p => p.id === id) || PRIORITIES[2];

  const getStatusColor = (status) => ({ sent: COLORS.success, draft: COLORS.warning, archived: COLORS.textLight, pending: COLORS.purple }[status] || COLORS.textLight);

  // ── Filtered data ──
  const filteredData = useMemo(() => {
    let data;
    if (activeTab === 'active')    data = letters.filter(l => l.status === 'sent');
    else if (activeTab === 'draft') data = drafts;
    else if (activeTab === 'archived') data = archived;
    else return [];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      data = data.filter(l =>
        l.subject?.toLowerCase().includes(q) ||
        l.recipient?.name?.toLowerCase().includes(q) ||
        l.recipient?.company?.toLowerCase().includes(q) ||
        l.letterNumber?.toLowerCase().includes(q) ||
        l.tags?.some(t => t.toLowerCase().includes(q))
      );
    }
    if (filters.type !== 'all')           data = data.filter(l => l.letterType === filters.type);
    if (filters.priority !== 'all')       data = data.filter(l => l.priority === filters.priority);
    if (filters.approvalStatus !== 'all') data = data.filter(l => l.approvalStatus === filters.approvalStatus);

    if (sortBy === 'newest')   data = [...data].sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt));
    else if (sortBy === 'oldest') data = [...data].sort((a,b) => new Date(a.createdAt) - new Date(b.createdAt));
    else if (sortBy === 'a-z') data = [...data].sort((a,b) => a.subject?.localeCompare(b.subject));
    else if (sortBy === 'priority') {
      const order = { urgent:0, high:1, normal:2, low:3 };
      data = [...data].sort((a,b) => (order[a.priority]||2)-(order[b.priority]||2));
    }
    return data;
  }, [activeTab, letters, drafts, archived, searchQuery, filters, sortBy]);

  // ── Analytics ──
  const analytics = useMemo(() => {
    const all = [...letters, ...drafts, ...archived];
    const byType = {};
    LETTER_TYPES.forEach(t => { byType[t.id] = all.filter(l => l.letterType === t.id).length; });
    const byMonth = {};
    all.forEach(l => {
      const m = l.createdAt?.substring(0,7) || '';
      byMonth[m] = (byMonth[m] || 0) + 1;
    });
    return {
      total: all.length,
      sent: letters.length,
      draft: drafts.length,
      archived: archived.length,
      pending: all.filter(l => l.approvalStatus === 'pending').length,
      totalViews: all.reduce((s,l) => s + (l.viewCount||0), 0),
      totalDownloads: all.reduce((s,l) => s + (l.downloadCount||0), 0),
      byType,
      byMonth,
      urgentPending: all.filter(l => l.priority === 'urgent' && l.approvalStatus === 'pending').length,
    };
  }, [letters, drafts, archived]);

  // ── Compose / Save / Send ──
  const openNewLetter = () => {
    setLetterForm(defaultLetterForm());
    setSelectedLetter(null);
    setComposeMode('new');
    setShowCompose(true);
  };

  const openEdit = (letter) => {
    setLetterForm({ ...defaultLetterForm(), ...letter });
    setSelectedLetter(letter);
    setComposeMode('edit');
    setShowDetail(false);
    setShowCompose(true);
  };

  const openReply = (letter) => {
    const form = defaultLetterForm();
    form.recipient = { ...letter.sender, name: letter.recipient.name, email: letter.recipient.email, phone: letter.recipient.phone };
    form.subject = `Re: ${letter.subject}`;
    form.salutation = letter.salutation;
    form.body = `\n\n--- Original Letter (${new Date(letter.date).toLocaleDateString()}) ---\n${letter.body}`;
    setLetterForm(form);
    setSelectedLetter(null);
    setComposeMode('reply');
    setShowDetail(false);
    setShowCompose(true);
  };

  const openForward = (letter) => {
    const form = { ...letter, id: undefined, letterNumber: genLetterNumber(), recipient: { name:'', title:'', company:'', address:'', email:'', phone:'' }, status:'draft', createdAt: new Date().toISOString(), subject: `Fwd: ${letter.subject}`, body: `\n\n--- Forwarded Letter ---\n${letter.body}` };
    setLetterForm(form);
    setSelectedLetter(null);
    setComposeMode('forward');
    setShowDetail(false);
    setShowCompose(true);
  };

  const saveDraft = async () => {
    if (!letterForm.subject.trim() && !letterForm.body.trim()) {
      Alert.alert('Empty Letter', 'Please add a subject or content before saving.');
      return;
    }
    try {
      const now = new Date().toISOString();
      const draft = {
        ...letterForm,
        id: selectedLetter?.id || `draft_${Date.now()}`,
        letterNumber: letterForm.letterNumber || genLetterNumber(),
        status: 'draft',
        approvalStatus: 'pending',
        createdAt: selectedLetter?.createdAt || now,
        lastModified: now,
        viewCount: 0, downloadCount: 0,
        versions: letterForm.versions?.length ? [...letterForm.versions, { version: `${(letterForm.versions.length+1)}.0`, date: now.split('T')[0], author: letterForm.sender.name, note: 'Updated draft' }] : [{ version: '1.0', date: now.split('T')[0], author: letterForm.sender.name, note: 'Initial draft' }],
      };
      let newDrafts;
      if (selectedLetter && drafts.find(d => d.id === selectedLetter.id)) {
        newDrafts = drafts.map(d => d.id === selectedLetter.id ? draft : d);
      } else {
        newDrafts = [draft, ...drafts];
      }
      setDrafts(newDrafts);
      await AsyncStorage.setItem('letter_drafts_v2', JSON.stringify(newDrafts));
      api.post(API_ENDPOINTS.drafts, draft).catch(() => {});
      setShowCompose(false);
      setActiveTab('draft');
      Alert.alert('Saved', 'Draft saved successfully.');
    } catch (e) {
      Alert.alert('Error', 'Failed to save draft.');
    }
  };

  const sendLetter = async () => {
    if (!letterForm.recipient.name.trim()) { Alert.alert('Required', 'Please enter recipient name.'); return; }
    if (!letterForm.subject.trim())         { Alert.alert('Required', 'Please enter a subject.');       return; }
    if (!letterForm.body.trim())            { Alert.alert('Required', 'Please enter letter content.');  return; }
    setIsSending(true);
    try {
      const now = new Date().toISOString();
      const letter = {
        ...letterForm,
        id: selectedLetter?.id && !selectedLetter.id.startsWith('draft_') ? selectedLetter.id : `ltr_${Date.now()}`,
        letterNumber: letterForm.letterNumber || genLetterNumber(),
        status: 'sent',
        approvalStatus: 'pending',
        createdAt: selectedLetter?.createdAt || now,
        sentAt: now,
        viewCount: 0, downloadCount: 0,
        versions: letterForm.versions?.length ? [...letterForm.versions, { version: `${(letterForm.versions.length+1)}.0`, date: now.split('T')[0], author: letterForm.sender.name, note: 'Sent' }] : [{ version: '1.0', date: now.split('T')[0], author: letterForm.sender.name, note: 'Initial send' }],
      };
      // Remove from drafts if was draft
      let newDrafts = drafts;
      if (selectedLetter && drafts.find(d => d.id === selectedLetter.id)) {
        newDrafts = drafts.filter(d => d.id !== selectedLetter.id);
        setDrafts(newDrafts);
      }
      // Update or add to letters
      let newLetters;
      if (!selectedLetter?.id?.startsWith('draft_') && letters.find(l => l.id === selectedLetter?.id)) {
        newLetters = letters.map(l => l.id === selectedLetter.id ? letter : l);
      } else {
        newLetters = [letter, ...letters];
      }
      setLetters(newLetters);
      await saveAll(newLetters, newDrafts);
      api.post(API_ENDPOINTS.letters, letter).catch(() => {});
      setShowCompose(false);
      setActiveTab('active');
      Alert.alert('Sent!', `Letter ${letter.letterNumber} sent successfully.`);
    } catch (e) {
      Alert.alert('Error', 'Failed to send letter.');
    } finally {
      setIsSending(false);
    }
  };

  const archiveLetter = async (letter) => {
    Alert.alert('Archive', `Archive "${letter.subject}"?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Archive', onPress: async () => {
        const updated = { ...letter, status: 'archived' };
        const newLetters = letters.filter(l => l.id !== letter.id);
        const newDrafts  = drafts.filter(d => d.id !== letter.id);
        const newArch    = [updated, ...archived];
        setLetters(newLetters); setDrafts(newDrafts); setArchived(newArch);
        await saveAll(newLetters, newDrafts, newArch);
        api.post(API_ENDPOINTS.letterArchive(letter.id)).catch(() => {});
        setShowDetail(false);
        Alert.alert('Archived', 'Letter archived.');
      }},
    ]);
  };

  const restoreLetter = async (letter) => {
    const updated = { ...letter, status: 'sent' };
    const newArch = archived.filter(a => a.id !== letter.id);
    const newLetters = [updated, ...letters];
    setArchived(newArch); setLetters(newLetters);
    await saveAll(newLetters, drafts, newArch);
    api.post(API_ENDPOINTS.letterRestore(letter.id)).catch(() => {});
    setShowDetail(false);
    Alert.alert('Restored', 'Letter restored to Active.');
  };

  const deleteLetter = (letter) => {
    Alert.alert('Delete', `Permanently delete "${letter.subject}"?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: async () => {
        const newLetters  = letters.filter(l => l.id !== letter.id);
        const newDrafts   = drafts.filter(d  => d.id !== letter.id);
        const newArchived = archived.filter(a => a.id !== letter.id);
        setLetters(newLetters); setDrafts(newDrafts); setArchived(newArchived);
        await saveAll(newLetters, newDrafts, newArchived);
        api.delete(API_ENDPOINTS.letterById(letter.id)).catch(() => {});
        setShowDetail(false);
        Alert.alert('Deleted', 'Letter deleted.');
      }},
    ]);
  };

  const approveLetter = async (letter) => {
    const update = (arr) => arr.map(l => l.id === letter.id ? { ...l, approvalStatus: 'approved', approvedBy: 'Current User', approvalDate: new Date().toISOString().split('T')[0] } : l);
    const nl = update(letters); const nd = update(drafts); const na = update(archived);
    setLetters(nl); setDrafts(nd); setArchived(na);
    setSelectedLetter(prev => prev ? { ...prev, approvalStatus: 'approved', approvedBy: 'Current User', approvalDate: new Date().toISOString().split('T')[0] } : prev);
    await saveAll(nl, nd, na);
    api.post(API_ENDPOINTS.letterApprove(letter.id)).catch(() => {});
    Alert.alert('Approved', 'Letter approved.');
  };

  const duplicateLetter = (letter) => {
    const form = { ...letter, id: undefined, letterNumber: genLetterNumber(), createdAt: new Date().toISOString(), status: 'draft', approvalStatus: 'pending', sentAt: undefined, subject: `Copy of ${letter.subject}` };
    setLetterForm(form);
    setSelectedLetter(null);
    setComposeMode('new');
    setShowDetail(false);
    setShowCompose(true);
  };

  // ── AI Generate ──
  const handleAIGenerate = async () => {
    if (!aiPrompt.trim() && !letterForm.subject.trim()) {
      Alert.alert('Info needed', 'Enter a subject or AI prompt first.'); return;
    }
    setIsGenAI(true);
    try {
      const body = await generateAILetter(aiPrompt, letterForm.letterType, letterForm.recipient.name, letterForm.subject);
      setLetterForm(prev => ({ ...prev, body }));
      setShowAiPrompt(false);
      setAiPrompt('');
    } catch (e) {
      Alert.alert('Error', 'Failed to generate letter. Please try again.');
    } finally {
      setIsGenAI(false);
    }
  };

  // ── PDF ──
  const handleGeneratePDF = async (letter) => {
    setIsGenPDF(true);
    try {
      const logoBase64 = await fetchLogoBase64(companyLogo || letter.letterhead?.logo);
      const html = buildLetterHTML(letter, logoBase64);
      const { uri } = await Print.printToFileAsync({ html, base64: false });
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(uri, { UTI: '.pdf', mimeType: 'application/pdf' });
      } else {
        Alert.alert('PDF Created', uri);
      }
      // Increment download count
      const update = (arr) => arr.map(l => l.id === letter.id ? { ...l, downloadCount: (l.downloadCount||0) + 1 } : l);
      const nl = update(letters); const nd = update(drafts); const na = update(archived);
      setLetters(nl); setDrafts(nd); setArchived(na);
      await saveAll(nl, nd, na);
    } catch (e) {
      Alert.alert('Error', 'Failed to generate PDF.');
    }
    setIsGenPDF(false);
  };

  const handlePrint = async (letter) => {
    setIsGenPDF(true);
    try {
      // Pre-fetch logo as base64 — external URL <img> tags cause the print renderer to hang
      const logoBase64 = await fetchLogoBase64(companyLogo || letter.letterhead?.logo);
      const html = buildLetterHTML(letter, logoBase64);
      // Generate file first — printAsync({ html }) hangs on complex HTML; file URI is reliable
      const { uri } = await Print.printToFileAsync({ html, base64: false });
      await Print.printAsync({ uri });
    } catch (e) {
      const msg = e?.message || '';
      // Ignore user-cancelled print dialogs
      if (
        msg !== 'Printing did not complete' &&
        !msg.includes('cancelled') &&
        !msg.includes('canceled')
      ) {
        console.warn('[handlePrint]', msg);
        Alert.alert('Print Failed', 'Could not send to printer. Try the PDF option instead.');
      }
    } finally {
      setIsGenPDF(false);
    }
  };

  const handleShare = async (letter) => {
    try {
      await Share.share({ title: letter.subject, message: `Letter: ${letter.subject}\nRef: ${letter.reference||'N/A'}\nDate: ${letter.date}\nTo: ${letter.recipient?.name} (${letter.recipient?.company})\n\n${letter.salutation},\n\n${letter.body}\n\n${letter.closing},\n${letter.signature}` });
    } catch (e) {
      Alert.alert('Error', 'Could not share letter.');
    }
  };

  // ── View detail ──
  const viewLetter = (letter) => {
    const update = (arr) => arr.map(l => l.id === letter.id ? { ...l, viewCount: (l.viewCount||0) + 1 } : l);
    const nl = update(letters); const nd = update(drafts); const na = update(archived);
    setLetters(nl); setDrafts(nd); setArchived(na);
    saveAll(nl, nd, na);
    setSelectedLetter({ ...letter, viewCount: (letter.viewCount||0) + 1 });
    setShowDetail(true);
  };

  // ── Add comment ──
  const addComment = async () => {
    if (!newComment.trim()) return;
    const comment = { id: `c_${Date.now()}`, author: 'Current User', text: newComment, date: new Date().toISOString() };
    const updated = { ...selectedLetter, comments: [...(selectedLetter.comments||[]), comment] };
    setSelectedLetter(updated);
    setNewComment('');
    setShowAddComment(false);
    const update = (arr) => arr.map(l => l.id === updated.id ? updated : l);
    const nl = update(letters); const nd = update(drafts); const na = update(archived);
    setLetters(nl); setDrafts(nd); setArchived(na);
    await saveAll(nl, nd, na);
  };

  // ── Add reminder ──
  const addReminder = async () => {
    if (!newReminder.date) { Alert.alert('Required', 'Please set a date.'); return; }
    const reminder = { id: `r_${Date.now()}`, ...newReminder };
    const updated = { ...selectedLetter, reminders: [...(selectedLetter.reminders||[]), reminder] };
    const reminderDate = newReminder.date; // capture before reset
    setSelectedLetter(updated);
    setNewReminder({ date: '', note: '' });
    setShowReminderModal(false);
    const update = (arr) => arr.map(l => l.id === updated.id ? updated : l);
    const nl = update(letters); const nd = update(drafts); const na = update(archived);
    setLetters(nl); setDrafts(nd); setArchived(na);
    await saveAll(nl, nd, na);
    Alert.alert('Reminder set', `Reminder for ${reminderDate}`);
  };

  // ── Bulk actions ──
  const toggleBulkSelect = (id) => setBulkSelected(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  const bulkArchive = async () => {
    const ids = new Set(bulkSelected);
    const nl = letters.map(l => ids.has(l.id) ? { ...l, status:'archived' } : l).filter(l => !ids.has(l.id));
    const archived2 = [...archived, ...letters.filter(l => ids.has(l.id)).map(l => ({...l, status:'archived'}))];
    setLetters(nl); setArchived(archived2);
    await saveAll(nl, drafts, archived2);
    setBulkSelected([]); setBulkMode(false);
  };
  const bulkDelete = () => {
    Alert.alert('Delete', `Delete ${bulkSelected.length} letters?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: async () => {
        const ids = new Set(bulkSelected);
        const nl = letters.filter(l => !ids.has(l.id));
        const nd = drafts.filter(d => !ids.has(d.id));
        const na = archived.filter(a => !ids.has(a.id));
        setLetters(nl); setDrafts(nd); setArchived(na);
        await saveAll(nl, nd, na);
        setBulkSelected([]); setBulkMode(false);
      }},
    ]);
  };

  // ── Apply template ──
  const applyTemplate = useCallback((tpl) => {
    const vars = tpl.variables || [];
    if (vars.length) {
      const initVals = {};
      vars.forEach(v => { initVals[v] = ''; });
      setVarValues(initVals);
      setSelectedTemplate(tpl);
      setShowTemplate(false);
      setShowVarFill(true);
    } else {
      setLetterForm(prev => ({ ...prev, letterType: tpl.letterType, subject: tpl.subject, salutation: tpl.salutation, body: tpl.body, closing: tpl.closing }));
      setShowTemplate(false);
    }
    // Increment usage
    const newTemplates = templates.map(t => t.id === tpl.id ? { ...t, usageCount: (t.usageCount||0)+1 } : t);
    setTemplates(newTemplates);
    AsyncStorage.setItem('letter_templates_v2', JSON.stringify(newTemplates));
    api.post(API_ENDPOINTS.letterTemplateUse(tpl.id)).catch(() => {});
  }, [templates]);

  const applyFilledTemplate = () => {
    if (!selectedTemplate) return;
    let body    = selectedTemplate.body;
    let subject = selectedTemplate.subject;
    Object.entries(varValues).forEach(([k,v]) => {
      const re = new RegExp(`{{${k}}}`, 'g');
      body    = body.replace(re, v || `[${k}]`);
      subject = subject.replace(re, v || `[${k}]`);
    });
    setLetterForm(prev => ({ ...prev, letterType: selectedTemplate.letterType, subject, salutation: selectedTemplate.salutation, body, closing: selectedTemplate.closing }));
    setShowVarFill(false);
    setSelectedTemplate(null);
  };

  // ─── Render helpers ───────────────────────────────────────────────────────
  const renderPriorityBadge = (priority) => {
    const p = getPriorityInfo(priority);
    return (
      <View style={[styles.priorityBadge, { backgroundColor: `${p.color}22`, borderColor: `${p.color}66` }]}>
        <Text style={[styles.priorityBadgeText, { color: p.color }]}>{p.label}</Text>
      </View>
    );
  };

  const renderTypeBadge = (type) => {
    const t = getTypeInfo(type);
    return (
      <View style={[styles.typeBadge, { backgroundColor: `${t.color}18` }]}>
        <MaterialCommunityIcons name={t.icon} size={11} color={t.color} />
        <Text style={[styles.typeBadgeText, { color: t.color }]}>{t.label}</Text>
      </View>
    );
  };

  // ─── Header ───────────────────────────────────────────────────────────────
  const renderHeader = useCallback(() => (
    <LinearGradient colors={[COLORS.primary, '#1a6dcc']} start={{ x:0,y:0 }} end={{ x:1,y:1 }} style={styles.header}>
      <View style={styles.headerRow}>
        <TouchableOpacity style={styles.backBtn} onPress={() => navigation?.goBack()}>
          <Ionicons name="arrow-back" size={22} color="#fff" />
        </TouchableOpacity>
        <View style={styles.headerCenter}>
          <Text style={styles.headerTitle}>Letters Management</Text>
          <Text style={styles.headerSub}>{analytics.total} letters · {analytics.draft} drafts</Text>
        </View>
        <View style={styles.headerActions}>
          <TouchableOpacity style={styles.headerIconBtn} onPress={() => setShowAnalytics(true)}>
            <MaterialCommunityIcons name="chart-bar" size={20} color="#fff" />
          </TouchableOpacity>
          <TouchableOpacity style={styles.headerIconBtn} onPress={() => setShowFilter(true)}>
            <MaterialCommunityIcons name="filter-variant" size={20} color="#fff" />
          </TouchableOpacity>
          {companyLogo ? (
            <Image source={{ uri: companyLogo || undefined }} style={{ width: 34, height: 34, borderRadius: 17, marginLeft: 4, resizeMode: 'cover', borderWidth: 2, borderColor: 'rgba(255,255,255,0.4)' }} />
          ) : (
            <View style={{ width: 34, height: 34, borderRadius: 17, marginLeft: 4, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center', borderWidth: 2, borderColor: 'rgba(255,255,255,0.4)' }}>
              <Text style={{ color: '#fff', fontWeight: '700', fontSize: 11 }}>{companyInitials}</Text>
            </View>
          )}
        </View>
      </View>

      {/* Stats bar */}
      <View style={styles.statsBar}>
        {[
          { label: 'Sent', value: analytics.sent, icon: 'send' },
          { label: 'Drafts', value: analytics.draft, icon: 'file-edit-outline' },
          { label: 'Pending', value: analytics.pending, icon: 'clock-outline' },
          { label: 'Archived', value: analytics.archived, icon: 'archive-outline' },
        ].map(s => (
          <View key={s.label} style={styles.statItem}>
            <MaterialCommunityIcons name={s.icon} size={16} color="rgba(255,255,255,0.8)" />
            <Text style={styles.statValue}>{s.value}</Text>
            <Text style={styles.statLabel}>{s.label}</Text>
          </View>
        ))}
      </View>

      {/* Search */}
      <View style={styles.searchBar}>
        <Ionicons name="search" size={18} color="#94a3b8" />
        <TextInput
          style={styles.searchInput}
          placeholder="Search letters, recipients, references..."
          placeholderTextColor="#94a3b8"
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
        {searchQuery ? <TouchableOpacity onPress={() => setSearchQuery('')}><Ionicons name="close-circle" size={18} color="#94a3b8" /></TouchableOpacity> : null}
      </View>
    </LinearGradient>
  ), [analytics, companyLogo, companyInitials, searchQuery]);

  // ─── Tabs ─────────────────────────────────────────────────────────────────
  const renderTabs = useCallback(() => {
    const tabs = [
      { id: 'active',   label: 'Active',   icon: 'send',           count: letters.filter(l=>l.status==='sent').length },
      { id: 'draft',    label: 'Drafts',   icon: 'file-edit-outline', count: drafts.length },
      { id: 'archived', label: 'Archived', icon: 'archive-outline', count: archived.length },
      { id: 'templates',label: 'Templates',icon: 'content-copy',   count: templates.length },
    ];
    return (
      <View style={styles.tabsWrapper}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.tabsScroll}>
          {tabs.map(t => (
            <TouchableOpacity key={t.id} style={[styles.tab, activeTab===t.id && styles.tabActive]} onPress={() => setActiveTab(t.id)}>
              <MaterialCommunityIcons name={t.icon} size={16} color={activeTab===t.id ? COLORS.primary : '#64748b'} />
              <Text style={[styles.tabText, activeTab===t.id && styles.tabTextActive]}>{t.label}</Text>
              {t.count > 0 && <View style={[styles.tabBadge, activeTab===t.id && styles.tabBadgeActive]}><Text style={[styles.tabBadgeText, activeTab===t.id && { color: COLORS.primary }]}>{t.count}</Text></View>}
            </TouchableOpacity>
          ))}
        </ScrollView>
        {/* Sort + bulk */}
        <View style={styles.tabActions}>
          <TouchableOpacity style={styles.sortBtn} onPress={() => {
            const opts = ['newest','oldest','a-z','priority'];
            const labels = ['Newest','Oldest','A–Z','Priority'];
            Alert.alert('Sort by', '', opts.map((o,i) => ({ text: labels[i], onPress: () => setSortBy(o) })).concat([{ text: 'Cancel', style: 'cancel' }]));
          }}>
            <MaterialCommunityIcons name="sort" size={18} color={COLORS.primary} />
          </TouchableOpacity>
          {(activeTab==='active'||activeTab==='draft') && (
            <TouchableOpacity style={styles.sortBtn} onPress={() => setBulkMode(!bulkMode)}>
              <MaterialCommunityIcons name={bulkMode ? 'close' : 'checkbox-multiple-outline'} size={18} color={bulkMode ? COLORS.danger : COLORS.primary} />
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  }, [activeTab, letters, drafts, archived, templates, bulkMode]);

  // ─── Letter card ──────────────────────────────────────────────────────────
  // ─── Cards rendered via React.memo components at module level ─────────────────
  // ─── Templates list ───────────────────────────────────────────────────────
  // Stable callbacks for React.memo cards
  const onTemplatePress = useCallback((item) => {
    applyTemplate(item);        // fills letterForm with template content
    if (!showCompose) setShowCompose(true);  // open compose but DON'T reset form
    setShowTemplate(false);
  }, [applyTemplate, showCompose]);

  const onLetterPress = useCallback((item) => {
    if (bulkMode) { toggleBulkSelect(item.id); } else { viewLetter(item); }
  }, [bulkMode, toggleBulkSelect, viewLetter]);

  const onLetterLongPress = useCallback((item) => {
    setBulkMode(true); toggleBulkSelect(item.id);
  }, [toggleBulkSelect]);

  // ─── Bulk action bar ──────────────────────────────────────────────────────
  const renderBulkBar = useCallback(() => bulkMode && bulkSelected.length > 0 ? (
    <View style={styles.bulkBar}>
      <Text style={styles.bulkCount}>{bulkSelected.length} selected</Text>
      <TouchableOpacity style={styles.bulkAction} onPress={bulkArchive}><MaterialCommunityIcons name="archive" size={18} color={COLORS.warning} /><Text style={[styles.bulkActionText, { color: COLORS.warning }]}>Archive</Text></TouchableOpacity>
      <TouchableOpacity style={styles.bulkAction} onPress={bulkDelete}><MaterialCommunityIcons name="delete" size={18} color={COLORS.danger} /><Text style={[styles.bulkActionText, { color: COLORS.danger }]}>Delete</Text></TouchableOpacity>
      <TouchableOpacity onPress={() => { setBulkSelected([]); setBulkMode(false); }}><Ionicons name="close" size={22} color={COLORS.textMid} /></TouchableOpacity>
    </View>
  ) : null, [bulkMode, bulkSelected, bulkArchive, bulkDelete]);

  // ─── Main list ────────────────────────────────────────────────────────────
  // ─── COMPOSE MODAL ────────────────────────────────────────────────────────
  const renderComposeModal = () => (
    <Modal visible={showCompose} animationType="slide" onRequestClose={() => setShowCompose(false)}>
      <SafeAreaView style={styles.modalFull}>
        <KeyboardAvoidingView style={{ flex:1 }} behavior={Platform.OS==='ios'?'padding':'height'} keyboardVerticalOffset={80}>

          {/* Compose header */}
          <LinearGradient colors={[COLORS.primary,'#1a6dcc']} style={styles.composeHeader}>
            <TouchableOpacity onPress={() => setShowCompose(false)}><Ionicons name="close" size={24} color="#fff" /></TouchableOpacity>
            <Text style={styles.composeTitle}>{composeMode==='edit' ? 'Edit Letter' : composeMode==='reply' ? 'Reply' : composeMode==='forward' ? 'Forward' : 'New Letter'}</Text>
            <View style={styles.composeHeaderActions}>
              <TouchableOpacity style={styles.composeHeaderBtn} onPress={() => setShowAiPrompt(true)}>
                <MaterialCommunityIcons name="robot-outline" size={20} color="#fff" />
              </TouchableOpacity>
              <TouchableOpacity style={styles.composeHeaderBtn} onPress={() => setShowTemplate(true)}>
                <MaterialCommunityIcons name="content-copy" size={20} color="#fff" />
              </TouchableOpacity>
              <TouchableOpacity style={styles.composeHeaderBtn} onPress={() => setShowFormatting(true)}>
                <MaterialCommunityIcons name="format-size" size={20} color="#fff" />
              </TouchableOpacity>
            </View>
          </LinearGradient>

          <ScrollView style={styles.composeScroll} keyboardShouldPersistTaps="handled">
            <View style={styles.composeForm}>

              {/* Letter type chips */}
              <Text style={styles.formSectionTitle}>Letter Type</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.typeChipsRow}>
                {LETTER_TYPES.map(t => (
                  <TouchableOpacity key={t.id} style={[styles.typeChip, letterForm.letterType===t.id && { backgroundColor: t.color, borderColor: t.color }]}
                    onPress={() => setLetterForm(p => ({...p, letterType: t.id}))}>
                    <MaterialCommunityIcons name={t.icon} size={14} color={letterForm.letterType===t.id ? '#fff' : t.color} />
                    <Text style={[styles.typeChipText, letterForm.letterType===t.id && { color:'#fff' }]}>{t.label}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>

              {/* Priority */}
              <Text style={styles.formSectionTitle}>Priority</Text>
              <View style={styles.priorityRow}>
                {PRIORITIES.map(p => (
                  <TouchableOpacity key={p.id} style={[styles.priorityChip, letterForm.priority===p.id && { backgroundColor: p.color, borderColor: p.color }]}
                    onPress={() => setLetterForm(prev => ({...prev, priority: p.id}))}>
                    <Text style={[styles.priorityChipText, letterForm.priority===p.id && { color:'#fff' }]}>{p.label}</Text>
                  </TouchableOpacity>
                ))}
              </View>

              {/* Date / Reference */}
              <View style={styles.formRow}>
                <View style={[styles.formGroup, { flex:1, marginRight:8 }]}>
                  <Text style={styles.formLabel}>Date</Text>
                  <TextInput style={styles.input} value={letterForm.date} onChangeText={v => setLetterForm(p => ({...p, date:v}))} placeholder="YYYY-MM-DD" placeholderTextColor={COLORS.textLight} />
                </View>
                <View style={[styles.formGroup, { flex:1, marginLeft:8 }]}>
                  <Text style={styles.formLabel}>Reference No.</Text>
                  <TextInput style={styles.input} value={letterForm.reference} onChangeText={v => setLetterForm(p => ({...p, reference:v}))} placeholder="REF/XXX/2024" placeholderTextColor={COLORS.textLight} />
                </View>
              </View>

              {/* Recipient */}
              <View style={styles.sectionCard}>
                <View style={styles.sectionCardHeader}>
                  <Text style={styles.sectionCardTitle}>Recipient</Text>
                  <TouchableOpacity style={styles.sectionCardBtn} onPress={() => setShowClientPicker(true)}>
                    <MaterialCommunityIcons name="account-search" size={16} color={COLORS.primary} />
                    <Text style={styles.sectionCardBtnText}>From Clients</Text>
                  </TouchableOpacity>
                </View>
                <TextInput style={styles.input} placeholder="Full name *" placeholderTextColor={COLORS.textLight} value={letterForm.recipient.name} onChangeText={v => setLetterForm(p => ({...p, recipient:{...p.recipient, name:v}}))} />
                <View style={[styles.formRow, { marginTop:8 }]}>
                  <TextInput style={[styles.input, {flex:1,marginRight:4}]} placeholder="Title/Position" placeholderTextColor={COLORS.textLight} value={letterForm.recipient.title} onChangeText={v => setLetterForm(p => ({...p, recipient:{...p.recipient, title:v}}))} />
                  <TextInput style={[styles.input, {flex:1,marginLeft:4}]} placeholder="Company" placeholderTextColor={COLORS.textLight} value={letterForm.recipient.company} onChangeText={v => setLetterForm(p => ({...p, recipient:{...p.recipient, company:v}}))} />
                </View>
                <TextInput style={[styles.input, {marginTop:8}]} placeholder="Full address" placeholderTextColor={COLORS.textLight} value={letterForm.recipient.address} onChangeText={v => setLetterForm(p => ({...p, recipient:{...p.recipient, address:v}}))} multiline />
                <View style={[styles.formRow, { marginTop:8 }]}>
                  <TextInput style={[styles.input, {flex:1,marginRight:4}]} placeholder="Email" placeholderTextColor={COLORS.textLight} value={letterForm.recipient.email} onChangeText={v => setLetterForm(p => ({...p, recipient:{...p.recipient, email:v}}))} keyboardType="email-address" />
                  <TextInput style={[styles.input, {flex:1,marginLeft:4}]} placeholder="Phone" placeholderTextColor={COLORS.textLight} value={letterForm.recipient.phone} onChangeText={v => setLetterForm(p => ({...p, recipient:{...p.recipient, phone:v}}))} keyboardType="phone-pad" />
                </View>
              </View>

              {/* Letterhead toggle */}
              <View style={styles.sectionCard}>
                <View style={styles.sectionCardHeader}>
                  <Text style={styles.sectionCardTitle}>Letterhead</Text>
                  <Switch value={letterForm.letterhead?.enabled} onValueChange={v => setLetterForm(p => ({...p, letterhead:{...p.letterhead, enabled:v}}))} trackColor={{ false:'#e2e8f0', true:`${COLORS.primary}66` }} thumbColor={letterForm.letterhead?.enabled ? COLORS.primary : '#f4f4f5'} />
                </View>
                {letterForm.letterhead?.enabled && (
                  <>
                    <TextInput style={styles.input} placeholder="Company name" placeholderTextColor={COLORS.textLight} value={letterForm.letterhead.companyName} onChangeText={v => setLetterForm(p => ({...p, letterhead:{...p.letterhead, companyName:v}}))} />
                    <TextInput style={[styles.input,{marginTop:8}]} placeholder="Company address" placeholderTextColor={COLORS.textLight} value={letterForm.letterhead.address} onChangeText={v => setLetterForm(p => ({...p, letterhead:{...p.letterhead, address:v}}))} />
                    <View style={[styles.formRow,{marginTop:8}]}>
                      <TextInput style={[styles.input,{flex:1,marginRight:4}]} placeholder="Phone" placeholderTextColor={COLORS.textLight} value={letterForm.letterhead.phone} onChangeText={v => setLetterForm(p => ({...p, letterhead:{...p.letterhead, phone:v}}))} />
                      <TextInput style={[styles.input,{flex:1,marginLeft:4}]} placeholder="Email" placeholderTextColor={COLORS.textLight} value={letterForm.letterhead.email} onChangeText={v => setLetterForm(p => ({...p, letterhead:{...p.letterhead, email:v}}))} keyboardType="email-address" />
                    </View>
                    <TextInput style={[styles.input,{marginTop:8}]} placeholder="Website" placeholderTextColor={COLORS.textLight} value={letterForm.letterhead.website} onChangeText={v => setLetterForm(p => ({...p, letterhead:{...p.letterhead, website:v}}))} />
                  </>
                )}
              </View>

              {/* Salutation & Subject */}
              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Salutation</Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom:8 }}>
                  {SALUTATIONS.map(s => (
                    <TouchableOpacity key={s} style={[styles.salChip, letterForm.salutation===s && styles.salChipActive]} onPress={() => setLetterForm(p => ({...p, salutation:s}))}>
                      <Text style={[styles.salChipText, letterForm.salutation===s && styles.salChipTextActive]}>{s}</Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
                <TextInput style={styles.input} value={letterForm.salutation} onChangeText={v => setLetterForm(p => ({...p, salutation:v}))} placeholder="Salutation" placeholderTextColor={COLORS.textLight} />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Subject *</Text>
                <TextInput style={styles.input} value={letterForm.subject} onChangeText={v => setLetterForm(p => ({...p, subject:v}))} placeholder="Letter subject" placeholderTextColor={COLORS.textLight} />
              </View>

              {/* Body */}
              <View style={styles.formGroup}>
                <View style={styles.bodyLabelRow}>
                  <Text style={styles.formLabel}>Letter Body *</Text>
                  <TouchableOpacity style={styles.aiBtn} onPress={() => setShowAiPrompt(true)}>
                    <MaterialCommunityIcons name="robot-outline" size={14} color={COLORS.accent} />
                    <Text style={styles.aiBtnText}>AI Write</Text>
                  </TouchableOpacity>
                </View>
                <TextInput
                  style={[styles.input, styles.bodyInput]}
                  multiline
                  numberOfLines={12}
                  textAlignVertical="top"
                  value={letterForm.body}
                  onChangeText={v => setLetterForm(p => ({...p, body:v}))}
                  placeholder="Enter letter body..."
                  placeholderTextColor={COLORS.textLight}
                />
              </View>

              {/* Closing & Signature */}
              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Closing</Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom:8 }}>
                  {CLOSINGS.map(c => (
                    <TouchableOpacity key={c} style={[styles.salChip, letterForm.closing===c && styles.salChipActive]} onPress={() => setLetterForm(p => ({...p, closing:c}))}>
                      <Text style={[styles.salChipText, letterForm.closing===c && styles.salChipTextActive]}>{c}</Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
                <TextInput style={styles.input} value={letterForm.closing} onChangeText={v => setLetterForm(p => ({...p, closing:v}))} placeholder="Closing phrase" placeholderTextColor={COLORS.textLight} />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Signature</Text>
                <TextInput style={styles.input} value={letterForm.signature} onChangeText={v => setLetterForm(p => ({...p, signature:v}))} placeholder="Your name" placeholderTextColor={COLORS.textLight} />
              </View>

              {/* Enclosures */}
              <View style={styles.sectionCard}>
                <Text style={styles.sectionCardTitle}>Enclosures</Text>
                {letterForm.enclosures?.map((enc,i) => (
                  <View key={i} style={styles.listItemRow}>
                    <MaterialCommunityIcons name="paperclip" size={14} color={COLORS.textMid} />
                    <Text style={styles.listItemText}>{enc}</Text>
                    <TouchableOpacity onPress={() => setLetterForm(p => ({...p, enclosures: p.enclosures.filter((_,j) => j!==i)}))}>
                      <Ionicons name="close-circle" size={18} color={COLORS.danger} />
                    </TouchableOpacity>
                  </View>
                ))}
                <View style={[styles.formRow, { marginTop:8 }]}>
                  <TextInput style={[styles.input, { flex:1, marginRight:8 }]} placeholder="Add enclosure" placeholderTextColor={COLORS.textLight} value={newEnclosure} onChangeText={setNewEnclosure} />
                  <TouchableOpacity style={styles.addRowBtn} onPress={() => { if(newEnclosure.trim()){ setLetterForm(p => ({...p, enclosures:[...(p.enclosures||[]),newEnclosure.trim()]})); setNewEnclosure(''); }}}>
                    <Ionicons name="add" size={20} color="#fff" />
                  </TouchableOpacity>
                </View>
              </View>

              {/* CC Copies */}
              <View style={styles.sectionCard}>
                <Text style={styles.sectionCardTitle}>Copies (CC)</Text>
                {letterForm.copies?.map((cc,i) => (
                  <View key={i} style={styles.listItemRow}>
                    <Ionicons name="mail-outline" size={14} color={COLORS.textMid} />
                    <Text style={styles.listItemText}>{cc}</Text>
                    <TouchableOpacity onPress={() => setLetterForm(p => ({...p, copies: p.copies.filter((_,j) => j!==i)}))}>
                      <Ionicons name="close-circle" size={18} color={COLORS.danger} />
                    </TouchableOpacity>
                  </View>
                ))}
                <View style={[styles.formRow, { marginTop:8 }]}>
                  <TextInput style={[styles.input, { flex:1, marginRight:8 }]} placeholder="Add email address" placeholderTextColor={COLORS.textLight} value={newCopy} onChangeText={setNewCopy} keyboardType="email-address" />
                  <TouchableOpacity style={styles.addRowBtn} onPress={() => { if(newCopy.trim()){ setLetterForm(p => ({...p, copies:[...(p.copies||[]),newCopy.trim()]})); setNewCopy(''); }}}>
                    <Ionicons name="add" size={20} color="#fff" />
                  </TouchableOpacity>
                </View>
              </View>

              {/* Tags */}
              <View style={styles.sectionCard}>
                <Text style={styles.sectionCardTitle}>Tags</Text>
                <View style={styles.tagsWrap}>
                  {letterForm.tags?.map((tag,i) => (
                    <TouchableOpacity key={i} style={styles.tagEditable} onPress={() => setLetterForm(p => ({...p, tags:p.tags.filter((_,j)=>j!==i)}))}>
                      <Text style={styles.tagEditableText}>#{tag}</Text>
                      <Ionicons name="close" size={12} color={COLORS.primary} />
                    </TouchableOpacity>
                  ))}
                </View>
                <View style={[styles.formRow, { marginTop:8 }]}>
                  <TextInput style={[styles.input, { flex:1, marginRight:8 }]} placeholder="Add tag" placeholderTextColor={COLORS.textLight} value={newTag} onChangeText={setNewTag} />
                  <TouchableOpacity style={styles.addRowBtn} onPress={() => { if(newTag.trim()){ setLetterForm(p => ({...p, tags:[...(p.tags||[]),newTag.trim().toLowerCase()]})); setNewTag(''); }}}>
                    <Ionicons name="add" size={20} color="#fff" />
                  </TouchableOpacity>
                </View>
              </View>

            </View>
          </ScrollView>

          {/* Compose action buttons */}
          <View style={styles.composeActions}>
            <TouchableOpacity style={styles.draftBtn} onPress={saveDraft}>
              <MaterialCommunityIcons name="content-save-outline" size={18} color={COLORS.primary} />
              <Text style={styles.draftBtnText}>Save Draft</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.sendBtn, (isSending||isGenAI) && styles.sendBtnDisabled]} onPress={sendLetter} disabled={isSending}>
              {isSending ? <ActivityIndicator size="small" color="#fff" /> : <><Ionicons name="send" size={18} color="#fff" /><Text style={styles.sendBtnText}>Send Letter</Text></>}
            </TouchableOpacity>
          </View>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
  );

  // ─── AI PROMPT MODAL ──────────────────────────────────────────────────────
  const renderAIModal = () => (
    <Modal visible={showAiPrompt} transparent animationType="slide" onRequestClose={() => setShowAiPrompt(false)}>
      <View style={styles.bottomSheet}>
        <View style={styles.bottomSheetContent}>
          <View style={styles.sheetHandle} />
          <View style={styles.sheetHeader}>
            <MaterialCommunityIcons name="robot-outline" size={24} color={COLORS.accent} />
            <Text style={styles.sheetTitle}>AI Letter Writer</Text>
            <TouchableOpacity onPress={() => setShowAiPrompt(false)}><Ionicons name="close" size={24} color={COLORS.textMid} /></TouchableOpacity>
          </View>
          <Text style={styles.sheetSubtitle}>Describe what you want to say and AI will draft the letter body for you.</Text>
          <TextInput style={[styles.input, { minHeight:100, textAlignVertical:'top', marginBottom:16 }]} multiline placeholder="e.g. Write about following up on tender submission, emphasise our experience and competitive pricing..." placeholderTextColor={COLORS.textLight} value={aiPrompt} onChangeText={setAiPrompt} />
          <TouchableOpacity style={[styles.sendBtn, isGenAI && styles.sendBtnDisabled]} onPress={handleAIGenerate} disabled={isGenAI}>
            {isGenAI ? <ActivityIndicator size="small" color="#fff" /> : <><MaterialCommunityIcons name="robot-outline" size={18} color="#fff" /><Text style={styles.sendBtnText}>Generate with AI</Text></>}
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  // ─── FORMATTING MODAL ─────────────────────────────────────────────────────
  const renderFormattingModal = () => (
    <Modal visible={showFormatting} transparent animationType="slide" onRequestClose={() => setShowFormatting(false)}>
      <View style={styles.bottomSheet}>
        <View style={styles.bottomSheetContent}>
          <View style={styles.sheetHandle} />
          <View style={styles.sheetHeader}>
            <MaterialCommunityIcons name="format-size" size={24} color={COLORS.primary} />
            <Text style={styles.sheetTitle}>Formatting Options</Text>
            <TouchableOpacity onPress={() => setShowFormatting(false)}><Ionicons name="close" size={24} color={COLORS.textMid} /></TouchableOpacity>
          </View>
          <ScrollView>
            <Text style={styles.formLabel}>Font Family</Text>
            <View style={styles.fontChips}>
              {FONT_FAMILIES.map(f => (
                <TouchableOpacity key={f} style={[styles.fontChip, letterForm.formatting?.fontFamily===f && styles.fontChipActive]} onPress={() => setLetterForm(p => ({...p, formatting:{...p.formatting, fontFamily:f}}))}>
                  <Text style={[styles.fontChipText, letterForm.formatting?.fontFamily===f && styles.fontChipTextActive]}>{f}</Text>
                </TouchableOpacity>
              ))}
            </View>
            <Text style={[styles.formLabel,{marginTop:16}]}>Font Size: {letterForm.formatting?.fontSize || 12}pt</Text>
            <View style={styles.sliderRow}>
              {[9,10,11,12,14,16].map(s => (
                <TouchableOpacity key={s} style={[styles.sizeChip, letterForm.formatting?.fontSize===s && styles.sizeChipActive]} onPress={() => setLetterForm(p => ({...p, formatting:{...p.formatting, fontSize:s}}))}>
                  <Text style={[styles.sizeChipText, letterForm.formatting?.fontSize===s && { color:'#fff' }]}>{s}</Text>
                </TouchableOpacity>
              ))}
            </View>
            <Text style={[styles.formLabel,{marginTop:16}]}>Line Spacing: {letterForm.formatting?.lineSpacing || 1.5}</Text>
            <View style={styles.sliderRow}>
              {[1.0,1.15,1.5,2.0].map(s => (
                <TouchableOpacity key={s} style={[styles.sizeChip, letterForm.formatting?.lineSpacing===s && styles.sizeChipActive]} onPress={() => setLetterForm(p => ({...p, formatting:{...p.formatting, lineSpacing:s}}))}>
                  <Text style={[styles.sizeChipText, letterForm.formatting?.lineSpacing===s && { color:'#fff' }]}>{s}</Text>
                </TouchableOpacity>
              ))}
            </View>
            <Text style={[styles.formLabel,{marginTop:16}]}>Margins (cm)</Text>
            <View style={styles.marginsGrid}>
              {['top','bottom','left','right'].map(m => (
                <View key={m} style={styles.marginItem}>
                  <Text style={styles.marginLabel}>{m.charAt(0).toUpperCase()+m.slice(1)}</Text>
                  <TextInput style={[styles.input,{width:70,textAlign:'center'}]} value={String(letterForm.formatting?.margins?.[m] || 2.5)} onChangeText={v => setLetterForm(p => ({...p, formatting:{...p.formatting, margins:{...p.formatting?.margins, [m]: parseFloat(v)||2.5}}}))} keyboardType="numeric" />
                </View>
              ))}
            </View>
          </ScrollView>
          <TouchableOpacity style={[styles.sendBtn,{marginTop:16}]} onPress={() => setShowFormatting(false)}><Text style={styles.sendBtnText}>Apply</Text></TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  // ─── LETTER DETAIL MODAL ──────────────────────────────────────────────────
  const renderDetailModal = () => {
    if (!selectedLetter) return null;
    const typeInfo = getTypeInfo(selectedLetter.letterType);
    return (
      <Modal visible={showDetail} animationType="slide" onRequestClose={() => setShowDetail(false)}>
        <SafeAreaView style={styles.modalFull}>
          <LinearGradient colors={[COLORS.primary,'#1a6dcc']} style={styles.detailHeader}>
            <TouchableOpacity onPress={() => setShowDetail(false)}><Ionicons name="arrow-back" size={24} color="#fff" /></TouchableOpacity>
            <Text style={styles.detailHeaderTitle} numberOfLines={1}>{selectedLetter.subject}</Text>
            <TouchableOpacity onPress={() => handleGeneratePDF(selectedLetter)} style={{ marginRight: 8 }}>
              {isGenPDF ? <ActivityIndicator size="small" color="#fff" /> : <MaterialCommunityIcons name="file-pdf-box" size={24} color="#fff" />}
            </TouchableOpacity>
            <TouchableOpacity onPress={() => handlePrint(selectedLetter)}>
              <MaterialCommunityIcons name="printer" size={24} color="#fff" />
            </TouchableOpacity>
          </LinearGradient>

          <ScrollView style={styles.detailScroll}>
            {/* Meta row */}
            <View style={styles.detailMetaCard}>
              <View style={[styles.typeIcon, { backgroundColor:`${typeInfo.color}18` }]}>
                <MaterialCommunityIcons name={typeInfo.icon} size={28} color={typeInfo.color} />
              </View>
              <View style={styles.detailMetaInfo}>
                <Text style={styles.detailLetterNum}>{selectedLetter.letterNumber}</Text>
                <View style={styles.detailMetaRow}>
                  {renderTypeBadge(selectedLetter.letterType)}
                  {renderPriorityBadge(selectedLetter.priority)}
                  <View style={[styles.statusBadge, { backgroundColor:`${getStatusColor(selectedLetter.status)}18` }]}>
                    <Text style={[styles.statusBadgeText, { color: getStatusColor(selectedLetter.status) }]}>{selectedLetter.status?.toUpperCase()}</Text>
                  </View>
                </View>
              </View>
            </View>

            {/* Action bar */}
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.detailActionBar}>
              <TouchableOpacity style={styles.detailActionBtn} onPress={() => openEdit(selectedLetter)}>
                <MaterialCommunityIcons name="pencil-outline" size={18} color={COLORS.primary} /><Text style={[styles.detailActionText,{color:COLORS.primary}]}>Edit</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.detailActionBtn} onPress={() => openReply(selectedLetter)}>
                <MaterialCommunityIcons name="reply" size={18} color={COLORS.accent} /><Text style={[styles.detailActionText,{color:COLORS.accent}]}>Reply</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.detailActionBtn} onPress={() => openForward(selectedLetter)}>
                <MaterialCommunityIcons name="share" size={18} color={COLORS.purple} /><Text style={[styles.detailActionText,{color:COLORS.purple}]}>Forward</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.detailActionBtn} onPress={() => handleShare(selectedLetter)}>
                <Ionicons name="share-social-outline" size={18} color={COLORS.gold} /><Text style={[styles.detailActionText,{color:COLORS.gold}]}>Share</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.detailActionBtn} onPress={() => duplicateLetter(selectedLetter)}>
                <MaterialCommunityIcons name="content-copy" size={18} color={COLORS.textMid} /><Text style={styles.detailActionText}>Duplicate</Text>
              </TouchableOpacity>
              {selectedLetter.approvalStatus === 'pending' && (
                <TouchableOpacity style={styles.detailActionBtn} onPress={() => approveLetter(selectedLetter)}>
                  <MaterialCommunityIcons name="check-circle-outline" size={18} color={COLORS.success} /><Text style={[styles.detailActionText,{color:COLORS.success}]}>Approve</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity style={styles.detailActionBtn} onPress={() => setShowReminderModal(true)}>
                <MaterialCommunityIcons name="bell-plus-outline" size={18} color={COLORS.warning} /><Text style={[styles.detailActionText,{color:COLORS.warning}]}>Remind</Text>
              </TouchableOpacity>
              {selectedLetter.status !== 'archived' ? (
                <TouchableOpacity style={styles.detailActionBtn} onPress={() => archiveLetter(selectedLetter)}>
                  <MaterialCommunityIcons name="archive-outline" size={18} color={COLORS.textMid} /><Text style={styles.detailActionText}>Archive</Text>
                </TouchableOpacity>
              ) : (
                <TouchableOpacity style={styles.detailActionBtn} onPress={() => restoreLetter(selectedLetter)}>
                  <MaterialCommunityIcons name="restore" size={18} color={COLORS.success} /><Text style={[styles.detailActionText,{color:COLORS.success}]}>Restore</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity style={styles.detailActionBtn} onPress={() => deleteLetter(selectedLetter)}>
                <MaterialCommunityIcons name="delete-outline" size={18} color={COLORS.danger} /><Text style={[styles.detailActionText,{color:COLORS.danger}]}>Delete</Text>
              </TouchableOpacity>
            </ScrollView>

            {/* Details grid */}
            <View style={styles.detailSection}>
              <Text style={styles.detailSectionTitle}>Details</Text>
              {[
                { label:'Date',     value: new Date(selectedLetter.date).toLocaleDateString('en-GB',{weekday:'long',year:'numeric',month:'long',day:'numeric'}) },
                { label:'Reference', value: selectedLetter.reference || '—' },
                { label:'Approval', value: selectedLetter.approvalStatus ? selectedLetter.approvalStatus.charAt(0).toUpperCase()+selectedLetter.approvalStatus.slice(1) : '—' },
                { label:'Approved by', value: selectedLetter.approvedBy || '—' },
                { label:'Views',    value: String(selectedLetter.viewCount||0) },
                { label:'Downloads',value: String(selectedLetter.downloadCount||0) },
              ].map(r => (
                <View key={r.label} style={styles.detailRow}>
                  <Text style={styles.detailLabel}>{r.label}</Text>
                  <Text style={styles.detailValue}>{r.value}</Text>
                </View>
              ))}
            </View>

            {/* Recipient */}
            <View style={styles.detailSection}>
              <Text style={styles.detailSectionTitle}>Recipient</Text>
              <Text style={styles.detailValue}>{selectedLetter.recipient?.name}{selectedLetter.recipient?.title ? `, ${selectedLetter.recipient.title}` : ''}</Text>
              {selectedLetter.recipient?.company && <Text style={styles.detailSubValue}>{selectedLetter.recipient.company}</Text>}
              {selectedLetter.recipient?.address && <Text style={styles.detailSubValue}>{selectedLetter.recipient.address}</Text>}
              {selectedLetter.recipient?.email   && <Text style={styles.detailSubValue}>{selectedLetter.recipient.email}</Text>}
            </View>

            {/* Letter content */}
            <View style={styles.detailSection}>
              <Text style={styles.detailSectionTitle}>Letter Content</Text>
              <View style={styles.letterContentBox}>
                {selectedLetter.letterhead?.enabled && (
                  <View style={styles.letterheadPreview}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
                      {(companyLogo || selectedLetter.letterhead?.logo) ? (
                        <Image source={{ uri: (companyLogo || selectedLetter?.letterhead?.logo) || undefined }} style={{ width: 40, height: 40, borderRadius: 6, marginRight: 10, resizeMode: 'contain' }} />
                      ) : null}
                      <Text style={styles.letterheadName}>{selectedLetter.letterhead.companyName}</Text>
                    </View>
                    <Text style={styles.letterheadDetail}>{selectedLetter.letterhead.address}</Text>
                    <Text style={styles.letterheadDetail}>{selectedLetter.letterhead.phone} | {selectedLetter.letterhead.email}</Text>
                  </View>
                )}
                <Text style={styles.letterDate}>{new Date(selectedLetter.date).toLocaleDateString('en-GB',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}</Text>
                {selectedLetter.reference ? <Text style={styles.letterRef}>Ref: {selectedLetter.reference}</Text> : null}
                <Text style={styles.letterSalutation}>{selectedLetter.salutation},</Text>
                <Text style={styles.letterSubjectLine}>Re: {selectedLetter.subject}</Text>
                <Text style={styles.letterBody}>{selectedLetter.body}</Text>
                <Text style={styles.letterClosing}>{selectedLetter.closing},</Text>
                <Text style={styles.letterSignature}>{selectedLetter.signature}</Text>
                {selectedLetter.sender?.title ? <Text style={styles.letterSenderTitle}>{selectedLetter.sender.title}</Text> : null}
                {selectedLetter.enclosures?.length > 0 && (
                  <View style={styles.enclosuresBox}>
                    <Text style={styles.enclosuresLabel}>Enclosures:</Text>
                    {selectedLetter.enclosures.map((e,i) => <Text key={i} style={styles.enclosureItem}>• {e}</Text>)}
                  </View>
                )}
                {selectedLetter.copies?.length > 0 && (
                  <View style={styles.enclosuresBox}>
                    <Text style={styles.enclosuresLabel}>Copies to:</Text>
                    {selectedLetter.copies.map((c,i) => <Text key={i} style={styles.enclosureItem}>• {c}</Text>)}
                  </View>
                )}
              </View>
            </View>

            {/* Tags */}
            {selectedLetter.tags?.length > 0 && (
              <View style={styles.detailSection}>
                <Text style={styles.detailSectionTitle}>Tags</Text>
                <View style={styles.tagsWrap}>
                  {selectedLetter.tags.map((t,i) => <View key={i} style={styles.tagChip}><Text style={styles.tagText}>#{t}</Text></View>)}
                </View>
              </View>
            )}

            {/* Reminders */}
            <View style={styles.detailSection}>
              <View style={styles.detailSectionRow}>
                <Text style={styles.detailSectionTitle}>Reminders</Text>
                <TouchableOpacity onPress={() => setShowReminderModal(true)} style={styles.addBtn}><Ionicons name="add" size={18} color={COLORS.primary} /></TouchableOpacity>
              </View>
              {selectedLetter.reminders?.length ? selectedLetter.reminders.map((r,i) => (
                <View key={i} style={styles.reminderItem}>
                  <MaterialCommunityIcons name="bell-outline" size={16} color={COLORS.warning} />
                  <View style={{ flex:1, marginLeft:8 }}>
                    <Text style={styles.reminderDate}>{r.date}</Text>
                    {r.note ? <Text style={styles.reminderNote}>{r.note}</Text> : null}
                  </View>
                </View>
              )) : <Text style={styles.emptySmall}>No reminders set</Text>}
            </View>

            {/* Version history */}
            <View style={styles.detailSection}>
              <View style={styles.detailSectionRow}>
                <Text style={styles.detailSectionTitle}>Version History</Text>
                <TouchableOpacity onPress={() => setShowVersions(true)}><Text style={styles.viewAll}>View All</Text></TouchableOpacity>
              </View>
              {selectedLetter.versions?.slice(0,2).map((v,i) => (
                <View key={i} style={styles.versionItem}>
                  <View style={styles.versionBadge}><Text style={styles.versionBadgeText}>v{v.version}</Text></View>
                  <View style={{ flex:1, marginLeft:10 }}>
                    <Text style={styles.versionDate}>{v.date}</Text>
                    <Text style={styles.versionNote}>{v.note || v.changes}</Text>
                    <Text style={styles.versionAuthor}>by {v.author}</Text>
                  </View>
                </View>
              ))}
            </View>

            {/* Comments */}
            <View style={styles.detailSection}>
              <View style={styles.detailSectionRow}>
                <Text style={styles.detailSectionTitle}>Comments ({selectedLetter.comments?.length||0})</Text>
                <TouchableOpacity onPress={() => setShowAddComment(true)} style={styles.addBtn}><Ionicons name="add" size={18} color={COLORS.primary} /></TouchableOpacity>
              </View>
              {selectedLetter.comments?.length ? selectedLetter.comments.map((c,i) => (
                <View key={i} style={styles.commentItem}>
                  <View style={styles.commentAvatar}><Text style={styles.commentAvatarText}>{c.author.charAt(0)}</Text></View>
                  <View style={{ flex:1, marginLeft:10 }}>
                    <View style={styles.commentHeader}>
                      <Text style={styles.commentAuthor}>{c.author}</Text>
                      <Text style={styles.commentDate}>{new Date(c.date).toLocaleDateString()}</Text>
                    </View>
                    <Text style={styles.commentText}>{c.text}</Text>
                  </View>
                </View>
              )) : <Text style={styles.emptySmall}>No comments yet</Text>}
            </View>

          </ScrollView>
        </SafeAreaView>
      </Modal>
    );
  };

  // ─── FILTER MODAL ─────────────────────────────────────────────────────────
  const renderFilterModal = () => (
    <Modal visible={showFilter} transparent animationType="slide" onRequestClose={() => setShowFilter(false)}>
      <View style={styles.bottomSheet}>
        <View style={styles.bottomSheetContent}>
          <View style={styles.sheetHandle} />
          <View style={styles.sheetHeader}>
            <MaterialCommunityIcons name="filter-variant" size={24} color={COLORS.primary} />
            <Text style={styles.sheetTitle}>Filter Letters</Text>
            <TouchableOpacity onPress={() => setShowFilter(false)}><Ionicons name="close" size={24} color={COLORS.textMid} /></TouchableOpacity>
          </View>
          <ScrollView>
            <Text style={styles.formLabel}>Letter Type</Text>
            <View style={styles.filterChips}>
              {[{id:'all',label:'All'},...LETTER_TYPES].map(t => (
                <TouchableOpacity key={t.id} style={[styles.filterChip, filters.type===t.id && styles.filterChipActive]} onPress={() => setFilters(p => ({...p, type:t.id}))}>
                  <Text style={[styles.filterChipText, filters.type===t.id && styles.filterChipTextActive]}>{t.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
            <Text style={[styles.formLabel,{marginTop:16}]}>Priority</Text>
            <View style={styles.filterChips}>
              {[{id:'all',label:'All'},...PRIORITIES].map(p => (
                <TouchableOpacity key={p.id} style={[styles.filterChip, filters.priority===p.id && styles.filterChipActive]} onPress={() => setFilters(prev => ({...prev, priority:p.id}))}>
                  <Text style={[styles.filterChipText, filters.priority===p.id && styles.filterChipTextActive]}>{p.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
            <Text style={[styles.formLabel,{marginTop:16}]}>Approval Status</Text>
            <View style={styles.filterChips}>
              {['all','pending','approved','rejected'].map(s => (
                <TouchableOpacity key={s} style={[styles.filterChip, filters.approvalStatus===s && styles.filterChipActive]} onPress={() => setFilters(p => ({...p, approvalStatus:s}))}>
                  <Text style={[styles.filterChipText, filters.approvalStatus===s && styles.filterChipTextActive]}>{s.charAt(0).toUpperCase()+s.slice(1)}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </ScrollView>
          <View style={styles.sheetActions}>
            <TouchableOpacity style={styles.resetBtn} onPress={() => setFilters({type:'all',priority:'all',approvalStatus:'all',dateRange:'all'})}><Text style={styles.resetBtnText}>Reset</Text></TouchableOpacity>
            <TouchableOpacity style={[styles.sendBtn,{flex:1}]} onPress={() => setShowFilter(false)}><Text style={styles.sendBtnText}>Apply Filters</Text></TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  // ─── ANALYTICS MODAL ──────────────────────────────────────────────────────
  const renderAnalyticsModal = () => (
    <Modal visible={showAnalytics} animationType="slide" onRequestClose={() => setShowAnalytics(false)}>
      <SafeAreaView style={styles.modalFull}>
        <LinearGradient colors={[COLORS.primary,'#1a6dcc']} style={styles.composeHeader}>
          <TouchableOpacity onPress={() => setShowAnalytics(false)}><Ionicons name="close" size={24} color="#fff" /></TouchableOpacity>
          <Text style={styles.composeTitle}>Analytics & Insights</Text>
          <View style={{ width:40 }} />
        </LinearGradient>
        <ScrollView style={styles.detailScroll}>
          {/* KPI cards */}
          <View style={styles.kpiGrid}>
            {[
              { label:'Total Letters', value: analytics.total,         icon:'email-outline',      color:COLORS.primary },
              { label:'Sent',          value: analytics.sent,          icon:'send',               color:COLORS.success },
              { label:'Drafts',        value: analytics.draft,         icon:'file-edit-outline',  color:COLORS.warning },
              { label:'Pending Approval', value: analytics.pending,   icon:'clock-alert-outline', color:COLORS.danger },
              { label:'Total Views',   value: analytics.totalViews,    icon:'eye-outline',        color:COLORS.accent },
              { label:'Downloads',     value: analytics.totalDownloads,icon:'download-outline',   color:COLORS.purple },
            ].map(k => (
              <View key={k.label} style={styles.kpiCard}>
                <MaterialCommunityIcons name={k.icon} size={28} color={k.color} />
                <Text style={[styles.kpiValue, { color:k.color }]}>{k.value}</Text>
                <Text style={styles.kpiLabel}>{k.label}</Text>
              </View>
            ))}
          </View>

          {/* By type */}
          <View style={styles.analyticsSection}>
            <Text style={styles.analyticsSectionTitle}>Letters by Type</Text>
            {LETTER_TYPES.filter(t => (analytics.byType[t.id]||0) > 0).map(t => {
              const count = analytics.byType[t.id] || 0;
              const pct = analytics.total ? (count / analytics.total) * 100 : 0;
              return (
                <View key={t.id} style={styles.barRow}>
                  <View style={styles.barLabel}>
                    <MaterialCommunityIcons name={t.icon} size={14} color={t.color} />
                    <Text style={styles.barLabelText}>{t.label}</Text>
                  </View>
                  <View style={styles.barTrack}>
                    <View style={[styles.barFill, { width:`${pct}%`, backgroundColor:t.color }]} />
                  </View>
                  <Text style={styles.barCount}>{count}</Text>
                </View>
              );
            })}
          </View>

          {/* Priorities */}
          <View style={styles.analyticsSection}>
            <Text style={styles.analyticsSectionTitle}>Letters by Priority</Text>
            {PRIORITIES.map(p => {
              const all = [...letters,...drafts,...archived];
              const count = all.filter(l => l.priority === p.id).length;
              const pct = analytics.total ? (count / analytics.total) * 100 : 0;
              return (
                <View key={p.id} style={styles.barRow}>
                  <View style={styles.barLabel}>
                    <Text style={[styles.barLabelText, { color:p.color }]}>{p.label}</Text>
                  </View>
                  <View style={styles.barTrack}>
                    <View style={[styles.barFill, { width:`${pct}%`, backgroundColor:p.color }]} />
                  </View>
                  <Text style={styles.barCount}>{count}</Text>
                </View>
              );
            })}
          </View>

          {/* Top senders/recipients */}
          <View style={styles.analyticsSection}>
            <Text style={styles.analyticsSectionTitle}>Recent Activity</Text>
            {[...letters,...drafts].slice(0,5).map((l,i) => (
              <View key={l.id||i} style={styles.activityItem}>
                <View style={[styles.activityDot, { backgroundColor: getStatusColor(l.status) }]} />
                <View style={{ flex:1, marginLeft:10 }}>
                  <Text style={styles.activityText} numberOfLines={1}>{l.subject}</Text>
                  <Text style={styles.activityDate}>{new Date(l.createdAt).toLocaleDateString()}</Text>
                </View>
                <View style={[styles.statusBadge, { backgroundColor:`${getStatusColor(l.status)}18` }]}>
                  <Text style={[styles.statusBadgeText, { color:getStatusColor(l.status) }]}>{l.status?.toUpperCase()}</Text>
                </View>
              </View>
            ))}
          </View>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  // ─── CLIENT PICKER MODAL ──────────────────────────────────────────────────
  const renderClientPickerModal = () => (
    <Modal visible={showClientPicker} transparent animationType="slide" onRequestClose={() => setShowClientPicker(false)}>
      <View style={styles.bottomSheet}>
        <View style={styles.bottomSheetContent}>
          <View style={styles.sheetHandle} />
          <View style={styles.sheetHeader}>
            <MaterialCommunityIcons name="account-group-outline" size={24} color={COLORS.primary} />
            <Text style={styles.sheetTitle}>Select Client</Text>
            <TouchableOpacity onPress={() => setShowClientPicker(false)}><Ionicons name="close" size={24} color={COLORS.textMid} /></TouchableOpacity>
          </View>
          <FlatList data={clients} keyExtractor={c => c.id} renderItem={({ item: cl }) => (
            <TouchableOpacity style={styles.clientItem} onPress={() => {
              setLetterForm(p => ({ ...p, recipient: { name: cl.name, title: cl.title||'', company: cl.company||'', address: cl.address||'', email: cl.email||'', phone: cl.phone||'' } }));
              setShowClientPicker(false);
            }}>
              <View style={styles.clientAvatar}><Text style={styles.clientAvatarText}>{cl.name.charAt(0)}</Text></View>
              <View style={{ flex:1, marginLeft:12 }}>
                <Text style={styles.clientName}>{cl.name}</Text>
                <Text style={styles.clientDetail}>{cl.title && cl.company ? `${cl.title} · ${cl.company}` : cl.company || cl.title}</Text>
              </View>
              <Ionicons name="chevron-forward" size={20} color={COLORS.textLight} />
            </TouchableOpacity>
          )} />
        </View>
      </View>
    </Modal>
  );

  // ─── TEMPLATE PICKER MODAL ────────────────────────────────────────────────
  const renderTemplateModal = () => (
    <Modal visible={showTemplate} animationType="slide" onRequestClose={() => setShowTemplate(false)}>
      <SafeAreaView style={styles.modalFull}>
        <LinearGradient colors={[COLORS.primary,'#1a6dcc']} style={styles.composeHeader}>
          <TouchableOpacity onPress={() => setShowTemplate(false)}><Ionicons name="close" size={24} color="#fff" /></TouchableOpacity>
          <Text style={styles.composeTitle}>Choose Template</Text>
          <View style={{ width:40 }} />
        </LinearGradient>
        <FlatList
          data={templates}
          keyExtractor={t => t.id}
          renderItem={({ item }) => <LetterTemplateCard item={item} onPress={onTemplatePress} />}
          contentContainerStyle={{ padding:16, paddingBottom:40 }}
          ListEmptyComponent={<View style={styles.empty}><Text style={styles.emptyText}>No templates</Text></View>}
          initialNumToRender={8}
          maxToRenderPerBatch={6}
          windowSize={5}
          removeClippedSubviews={true}
        />
      </SafeAreaView>
    </Modal>
  );

  // ─── VARIABLE FILL MODAL ──────────────────────────────────────────────────
  const renderVarFillModal = () => (
    <Modal visible={showVarFill} transparent animationType="slide" onRequestClose={() => setShowVarFill(false)}>
      <View style={styles.bottomSheet}>
        <View style={[styles.bottomSheetContent, { maxHeight: height * 0.85 }]}>
          <View style={styles.sheetHandle} />
          <View style={styles.sheetHeader}>
            <MaterialCommunityIcons name="variable" size={24} color={COLORS.purple} />
            <Text style={styles.sheetTitle}>Fill Template Variables</Text>
            <TouchableOpacity onPress={() => setShowVarFill(false)}><Ionicons name="close" size={24} color={COLORS.textMid} /></TouchableOpacity>
          </View>
          <ScrollView>
            {selectedTemplate?.variables?.map(v => (
              <View key={v} style={styles.formGroup}>
                <Text style={styles.formLabel}>{v.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}</Text>
                <TextInput style={styles.input} placeholder={`Enter ${v.replace(/_/g,' ')}`} placeholderTextColor={COLORS.textLight} value={varValues[v]||''} onChangeText={t => setVarValues(p => ({...p,[v]:t}))} />
              </View>
            ))}
          </ScrollView>
          <TouchableOpacity style={[styles.sendBtn,{marginTop:16}]} onPress={applyFilledTemplate}><Text style={styles.sendBtnText}>Apply Template</Text></TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  // ─── VERSION HISTORY MODAL ────────────────────────────────────────────────
  const renderVersionsModal = () => (
    <Modal visible={showVersions} transparent animationType="slide" onRequestClose={() => setShowVersions(false)}>
      <View style={styles.bottomSheet}>
        <View style={styles.bottomSheetContent}>
          <View style={styles.sheetHandle} />
          <View style={styles.sheetHeader}>
            <MaterialCommunityIcons name="history" size={24} color={COLORS.primary} />
            <Text style={styles.sheetTitle}>Version History</Text>
            <TouchableOpacity onPress={() => setShowVersions(false)}><Ionicons name="close" size={24} color={COLORS.textMid} /></TouchableOpacity>
          </View>
          <FlatList
            data={selectedLetter?.versions || []}
            keyExtractor={(v,i) => String(i)}
            renderItem={({ item: v, index: i }) => (
              <View style={styles.versionItem}>
                <View style={styles.versionBadge}><Text style={styles.versionBadgeText}>v{v.version}</Text></View>
                <View style={{ flex:1, marginLeft:10 }}>
                  <Text style={styles.versionDate}>{v.date}</Text>
                  <Text style={styles.versionNote}>{v.note || v.changes}</Text>
                  <Text style={styles.versionAuthor}>by {v.author}</Text>
                </View>
                {i === 0 && <View style={styles.latestBadge}><Text style={styles.latestBadgeText}>Latest</Text></View>}
              </View>
            )}
            contentContainerStyle={{ paddingBottom:32 }}
          />
        </View>
      </View>
    </Modal>
  );

  // ─── REMINDER MODAL ───────────────────────────────────────────────────────
  const renderReminderModal = () => (
    <Modal visible={showReminderModal} transparent animationType="slide" onRequestClose={() => setShowReminderModal(false)}>
      <View style={styles.bottomSheet}>
        <View style={styles.bottomSheetContent}>
          <View style={styles.sheetHandle} />
          <View style={styles.sheetHeader}>
            <MaterialCommunityIcons name="bell-plus-outline" size={24} color={COLORS.warning} />
            <Text style={styles.sheetTitle}>Set Reminder</Text>
            <TouchableOpacity onPress={() => setShowReminderModal(false)}><Ionicons name="close" size={24} color={COLORS.textMid} /></TouchableOpacity>
          </View>
          <Text style={styles.formLabel}>Date (YYYY-MM-DD)</Text>
          <TextInput style={[styles.input,{marginBottom:12}]} placeholder="2024-12-31" placeholderTextColor={COLORS.textLight} value={newReminder.date} onChangeText={v => setNewReminder(p => ({...p, date:v}))} />
          <Text style={styles.formLabel}>Note</Text>
          <TextInput style={[styles.input,{marginBottom:16}]} placeholder="e.g. Follow up on tender decision" placeholderTextColor={COLORS.textLight} value={newReminder.note} onChangeText={v => setNewReminder(p => ({...p, note:v}))} />
          <TouchableOpacity style={styles.sendBtn} onPress={addReminder}><Text style={styles.sendBtnText}>Set Reminder</Text></TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  // ─── ADD COMMENT MODAL ────────────────────────────────────────────────────
  const renderAddCommentModal = () => (
    <Modal visible={showAddComment} transparent animationType="slide" onRequestClose={() => setShowAddComment(false)}>
      <View style={styles.bottomSheet}>
        <View style={styles.bottomSheetContent}>
          <View style={styles.sheetHandle} />
          <View style={styles.sheetHeader}>
            <MaterialCommunityIcons name="comment-plus-outline" size={24} color={COLORS.accent} />
            <Text style={styles.sheetTitle}>Add Comment</Text>
            <TouchableOpacity onPress={() => setShowAddComment(false)}><Ionicons name="close" size={24} color={COLORS.textMid} /></TouchableOpacity>
          </View>
          <TextInput style={[styles.input, { minHeight:80, textAlignVertical:'top', marginBottom:16 }]} multiline placeholder="Add your comment..." placeholderTextColor={COLORS.textLight} value={newComment} onChangeText={setNewComment} />
          <TouchableOpacity style={styles.sendBtn} onPress={addComment}><Text style={styles.sendBtnText}>Post Comment</Text></TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  // ─── LOADING ──────────────────────────────────────────────────────────────
  // ─── FlatList hooks — MUST be above any early returns (Rules of Hooks) ──────
  const listData = activeTab === 'templates' ? templates : filteredData;

  const renderLetterItem = useCallback(({ item }) => (
    <LetterCard
      item={item}
      bulkMode={bulkMode}
      bulkSelected={bulkSelected}
      onPress={onLetterPress}
      onLongPress={onLetterLongPress}
    />
  ), [bulkMode, bulkSelected, onLetterPress, onLetterLongPress]);

  const renderTemplateItem = useCallback(({ item }) => (
    <LetterTemplateCard item={item} onPress={onTemplatePress} />
  ), [onTemplatePress]);

  const renderListItem = activeTab === 'templates' ? renderTemplateItem : renderLetterItem;

  const ListHeader = useCallback(() => (
    <>
      {renderHeader()}
      {renderTabs()}
      {renderBulkBar()}
    </>
  ), [renderHeader, renderTabs, renderBulkBar]);

  const ListEmpty = useCallback(() => (
    <Animated.View style={{ opacity: fadeAnim }}>
      {activeTab === 'templates' ? (
        <View style={styles.empty}>
          <MaterialCommunityIcons name="content-copy" size={52} color={COLORS.border} />
          <Text style={styles.emptyText}>No templates</Text>
        </View>
      ) : (
        <View style={styles.empty}>
          <MaterialCommunityIcons name="email-outline" size={52} color={COLORS.border} />
          <Text style={styles.emptyText}>No letters found</Text>
          <Text style={styles.emptySubtext}>{searchQuery ? 'Try a different search' : 'Tap + to create your first letter'}</Text>
        </View>
      )}
    </Animated.View>
  ), [activeTab, searchQuery, fadeAnim]);

  if (isLoading) {
    return (
      <View style={styles.loader}>
        <ActivityIndicator size="large" color={COLORS.primary} />
        <Text style={styles.loaderText}>Loading letters...</Text>
      </View>
    );
  }

  // ─── MAIN RENDER ──────────────────────────────────────────────────────────
  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <FlatList
        data={listData}
        keyExtractor={item => item.id}
        renderItem={renderListItem}
        ListHeaderComponent={ListHeader}
        ListEmptyComponent={ListEmpty}
        contentContainerStyle={{ paddingBottom: 100 }}
        showsVerticalScrollIndicator={false}
        initialNumToRender={10}
        maxToRenderPerBatch={8}
        windowSize={7}
        removeClippedSubviews={true}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={async () => { setRefreshing(true); await init(); setRefreshing(false); }} colors={[COLORS.primary]} tintColor={COLORS.primary} />}
      />

      {/* FAB */}
      <TouchableOpacity style={styles.fab} onPress={openNewLetter}>
        <LinearGradient colors={[COLORS.primary,'#1a6dcc']} style={styles.fabGradient}>
          <Ionicons name="add" size={28} color="#fff" />
        </LinearGradient>
      </TouchableOpacity>

      {/* All modals — gated so heavy JSX is only built when open */}
      {showCompose        && renderComposeModal()}
      {showDetail         && renderDetailModal()}
      {showFilter         && renderFilterModal()}
      {showAnalytics      && renderAnalyticsModal()}
      {showTemplate       && renderTemplateModal()}
      {showClientPicker   && renderClientPickerModal()}
      {showAiPrompt       && renderAIModal()}
      {showFormatting     && renderFormattingModal()}
      {showVersions       && renderVersionsModal()}
      {showReminderModal  && renderReminderModal()}
      {showAddComment     && renderAddCommentModal()}
      {showVarFill        && renderVarFillModal()}
    </SafeAreaView>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  container:   { flex:1, backgroundColor: COLORS.bg },
  scroll:      { flex:1 },
  loader:      { flex:1, justifyContent:'center', alignItems:'center', backgroundColor: COLORS.bg },
  loaderText:  { marginTop:12, fontSize:15, color: COLORS.textMid },

  // Header
  header:      { paddingTop:12, paddingBottom:16, paddingHorizontal:16 },
  headerRow:   { flexDirection:'row', alignItems:'center', marginBottom:12 },
  backBtn:     { width:38, height:38, borderRadius:19, backgroundColor:'rgba(255,255,255,0.2)', justifyContent:'center', alignItems:'center' },
  headerCenter:{ flex:1, marginHorizontal:12 },
  headerTitle: { fontSize:18, fontWeight:'700', color:'#fff' },
  headerSub:   { fontSize:12, color:'rgba(255,255,255,0.75)', marginTop:1 },
  headerActions:{ flexDirection:'row', gap:8 },
  headerIconBtn:{ width:36, height:36, borderRadius:18, backgroundColor:'rgba(255,255,255,0.2)', justifyContent:'center', alignItems:'center' },
  statsBar:    { flexDirection:'row', justifyContent:'space-around', marginBottom:12 },
  statItem:    { alignItems:'center', gap:2 },
  statValue:   { fontSize:18, fontWeight:'700', color:'#fff' },
  statLabel:   { fontSize:10, color:'rgba(255,255,255,0.8)' },
  searchBar:   { flexDirection:'row', alignItems:'center', backgroundColor:'rgba(255,255,255,0.9)', borderRadius:10, paddingHorizontal:12, paddingVertical:10, gap:8 },
  searchInput: { flex:1, fontSize:14, color: COLORS.textDark },

  // Tabs
  tabsWrapper:  { flexDirection:'row', alignItems:'center', backgroundColor:'#fff', borderBottomWidth:1, borderBottomColor: COLORS.border },
  tabsScroll:   { paddingHorizontal:4 },
  tab:          { flexDirection:'row', alignItems:'center', gap:6, paddingHorizontal:14, paddingVertical:12, borderBottomWidth:2, borderBottomColor:'transparent' },
  tabActive:    { borderBottomColor: COLORS.primary },
  tabText:      { fontSize:13, fontWeight:'500', color:'#64748b' },
  tabTextActive:{ color: COLORS.primary, fontWeight:'600' },
  tabBadge:     { backgroundColor:'#f1f5f9', paddingHorizontal:6, paddingVertical:1, borderRadius:10 },
  tabBadgeActive:{ backgroundColor:`${COLORS.primary}18` },
  tabBadgeText: { fontSize:10, fontWeight:'600', color:'#64748b' },
  tabActions:   { flexDirection:'row', gap:4, paddingRight:8 },
  sortBtn:      { width:34, height:34, borderRadius:17, backgroundColor:`${COLORS.primary}12`, justifyContent:'center', alignItems:'center' },

  // Cards
  content:      { padding:16, paddingBottom:100 },
  card:         { backgroundColor:'#fff', borderRadius:14, padding:14, marginBottom:12, borderWidth:1, borderColor: COLORS.border, shadowColor:'#000', shadowOffset:{width:0,height:2}, shadowOpacity:0.06, shadowRadius:6, elevation:2 },
  cardSelected: { borderColor: COLORS.primary, backgroundColor:`${COLORS.primary}06` },
  bulkCheck:    { position:'absolute', top:12, right:12, width:22, height:22, borderRadius:11, borderWidth:2, borderColor: COLORS.border, backgroundColor:'#fff', justifyContent:'center', alignItems:'center' },
  bulkCheckSelected:{ backgroundColor: COLORS.primary, borderColor: COLORS.primary },
  cardHeader:   { flexDirection:'row', alignItems:'flex-start', marginBottom:8 },
  typeIcon:     { width:44, height:44, borderRadius:22, justifyContent:'center', alignItems:'center', marginRight:10 },
  cardHeaderText:{ flex:1 },
  cardSubject:  { fontSize:15, fontWeight:'600', color: COLORS.textDark, marginBottom:2 },
  cardRecipient:{ fontSize:12, color: COLORS.textMid },
  cardMeta:     { alignItems:'flex-end', gap:4 },
  cardDate:     { fontSize:10, color: COLORS.textLight },
  cardPreview:  { fontSize:12, color: COLORS.textMid, lineHeight:17, marginBottom:8 },
  tagsRow:      { marginBottom:8 },
  tagChip:      { backgroundColor:`${COLORS.primary}12`, paddingHorizontal:8, paddingVertical:3, borderRadius:10, marginRight:6 },
  tagText:      { fontSize:10, fontWeight:'500', color: COLORS.primary },
  cardFooter:   { flexDirection:'row', justifyContent:'space-between', alignItems:'center' },
  cardFooterLeft:{ flexDirection:'row', alignItems:'center', gap:8 },
  cardFooterRight:{ flexDirection:'row', alignItems:'center', gap:8 },
  cardRef:      { fontSize:10, color: COLORS.textLight },
  cardStat:     { flexDirection:'row', alignItems:'center', gap:3 },
  cardStatText: { fontSize:10, color: COLORS.textLight },
  statusDot:    { width:8, height:8, borderRadius:4 },
  pendingBanner:{ flexDirection:'row', alignItems:'center', gap:6, backgroundColor:'#fef3c7', padding:8, borderRadius:8, marginTop:8 },
  pendingText:  { fontSize:11, fontWeight:'600', color: COLORS.warning },
  reminderBanner:{ flexDirection:'row', alignItems:'center', gap:6, backgroundColor:`${COLORS.accent}15`, padding:8, borderRadius:8, marginTop:4 },
  reminderText: { fontSize:11, fontWeight:'600', color: COLORS.accent },

  // Priority / type badges
  priorityBadge:{ paddingHorizontal:6, paddingVertical:2, borderRadius:8, borderWidth:1 },
  priorityBadgeText:{ fontSize:9, fontWeight:'700' },
  typeBadge:    { flexDirection:'row', alignItems:'center', gap:4, paddingHorizontal:7, paddingVertical:3, borderRadius:8 },
  typeBadgeText:{ fontSize:9, fontWeight:'600' },

  // Templates
  tplCard:      { flexDirection:'row', alignItems:'center', backgroundColor:'#fff', borderRadius:12, padding:14, marginBottom:10, borderWidth:1, borderColor: COLORS.border },
  tplIcon:      { width:48, height:48, borderRadius:24, justifyContent:'center', alignItems:'center', marginRight:12 },
  tplInfo:      { flex:1 },
  tplTitleRow:  { flexDirection:'row', alignItems:'center', gap:6, marginBottom:3 },
  tplName:      { fontSize:14, fontWeight:'600', color: COLORS.textDark },
  tplSubject:   { fontSize:12, color: COLORS.textMid, marginBottom:6 },
  tplFooter:    { flexDirection:'row', alignItems:'center', gap:8 },
  tplUsage:     { fontSize:11, color: COLORS.textLight },
  tplVars:      { fontSize:11, color: COLORS.purple },

  // Bulk bar
  bulkBar:      { flexDirection:'row', alignItems:'center', backgroundColor:'#fff', paddingHorizontal:16, paddingVertical:10, gap:16, borderTopWidth:1, borderTopColor: COLORS.border },
  bulkCount:    { fontSize:14, fontWeight:'600', color: COLORS.textDark, flex:1 },
  bulkAction:   { flexDirection:'row', alignItems:'center', gap:6 },
  bulkActionText:{ fontSize:13, fontWeight:'600' },

  // Empty
  empty:        { alignItems:'center', paddingVertical:60 },
  emptyText:    { fontSize:16, fontWeight:'600', color: COLORS.textMid, marginTop:12 },
  emptySubtext: { fontSize:13, color: COLORS.textLight, marginTop:6, textAlign:'center' },
  emptySmall:   { fontSize:12, color: COLORS.textLight, fontStyle:'italic', marginTop:4 },

  // FAB
  fab:          { position:'absolute', right:20, bottom:24, borderRadius:30, shadowColor:'#000', shadowOffset:{width:0,height:4}, shadowOpacity:0.25, shadowRadius:8, elevation:8 },
  fabGradient:  { width:58, height:58, borderRadius:29, justifyContent:'center', alignItems:'center' },

  // Modal full screen
  modalFull:    { flex:1, backgroundColor:'#fff' },
  composeHeader:{ flexDirection:'row', alignItems:'center', justifyContent:'space-between', paddingHorizontal:16, paddingVertical:14 },
  composeTitle: { fontSize:17, fontWeight:'700', color:'#fff', flex:1, textAlign:'center' },
  composeHeaderActions:{ flexDirection:'row', gap:8 },
  composeHeaderBtn:{ width:36, height:36, borderRadius:18, backgroundColor:'rgba(255,255,255,0.2)', justifyContent:'center', alignItems:'center' },
  composeScroll:{ flex:1 },
  composeForm:  { padding:16, paddingBottom:40 },

  // Form elements
  formSectionTitle:{ fontSize:13, fontWeight:'700', color: COLORS.textDark, marginBottom:10, marginTop:4, textTransform:'uppercase', letterSpacing:0.5 },
  formLabel:    { fontSize:12, fontWeight:'600', color: COLORS.textMid, marginBottom:6 },
  formGroup:    { marginBottom:16 },
  formRow:      { flexDirection:'row' },
  input:        { backgroundColor:'#f8fafc', borderRadius:10, borderWidth:1, borderColor: COLORS.border, paddingHorizontal:12, paddingVertical:12, fontSize:14, color: COLORS.textDark },
  bodyInput:    { minHeight:160, textAlignVertical:'top' },
  bodyLabelRow: { flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom:6 },
  aiBtn:        { flexDirection:'row', alignItems:'center', gap:5, backgroundColor:`${COLORS.accent}15`, paddingHorizontal:10, paddingVertical:5, borderRadius:8 },
  aiBtnText:    { fontSize:12, fontWeight:'600', color: COLORS.accent },

  // Type chips
  typeChipsRow: { marginBottom:16 },
  typeChip:     { flexDirection:'row', alignItems:'center', gap:5, paddingHorizontal:12, paddingVertical:7, borderRadius:20, borderWidth:1.5, borderColor: COLORS.border, marginRight:8, backgroundColor:'#fff' },
  typeChipText: { fontSize:12, fontWeight:'500', color: COLORS.textMid },

  // Priority chips
  priorityRow:  { flexDirection:'row', gap:8, marginBottom:16 },
  priorityChip: { flex:1, paddingVertical:9, borderRadius:10, borderWidth:1.5, borderColor: COLORS.border, alignItems:'center', backgroundColor:'#fff' },
  priorityChipText:{ fontSize:12, fontWeight:'600', color: COLORS.textMid },

  // Salutation/closing chips
  salChip:      { paddingHorizontal:12, paddingVertical:6, borderRadius:16, backgroundColor:'#f1f5f9', marginRight:8 },
  salChipActive:{ backgroundColor: COLORS.primary },
  salChipText:  { fontSize:12, color: COLORS.textMid },
  salChipTextActive:{ color:'#fff', fontWeight:'600' },

  // Section cards
  sectionCard:  { backgroundColor:'#f8fafc', borderRadius:12, padding:14, borderWidth:1, borderColor: COLORS.border, marginBottom:16 },
  sectionCardHeader:{ flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom:10 },
  sectionCardTitle:{ fontSize:13, fontWeight:'700', color: COLORS.textDark },
  sectionCardBtn:{ flexDirection:'row', alignItems:'center', gap:5 },
  sectionCardBtnText:{ fontSize:12, fontWeight:'600', color: COLORS.primary },

  // List items (enclosures, copies)
  listItemRow:  { flexDirection:'row', alignItems:'center', gap:8, marginBottom:6 },
  listItemText: { flex:1, fontSize:13, color: COLORS.textDark },
  addRowBtn:    { width:42, height:42, borderRadius:10, backgroundColor: COLORS.primary, justifyContent:'center', alignItems:'center' },

  // Tags editable
  tagsWrap:     { flexDirection:'row', flexWrap:'wrap', gap:6, marginBottom:4 },
  tagEditable:  { flexDirection:'row', alignItems:'center', gap:5, backgroundColor:`${COLORS.primary}15`, paddingHorizontal:10, paddingVertical:5, borderRadius:12 },
  tagEditableText:{ fontSize:12, fontWeight:'500', color: COLORS.primary },

  // Compose actions
  composeActions:{ flexDirection:'row', gap:12, padding:16, borderTopWidth:1, borderTopColor: COLORS.border, backgroundColor:'#fff' },
  draftBtn:     { flex:1, flexDirection:'row', alignItems:'center', justifyContent:'center', gap:8, borderRadius:12, borderWidth:1.5, borderColor: COLORS.primary, paddingVertical:14 },
  draftBtnText: { fontSize:14, fontWeight:'600', color: COLORS.primary },
  sendBtn:      { flex:1, flexDirection:'row', alignItems:'center', justifyContent:'center', gap:8, borderRadius:12, backgroundColor: COLORS.primary, paddingVertical:14 },
  sendBtnText:  { fontSize:14, fontWeight:'600', color:'#fff' },
  sendBtnDisabled:{ opacity:0.6 },

  // Detail modal
  detailHeader: { flexDirection:'row', alignItems:'center', gap:12, paddingHorizontal:16, paddingVertical:14 },
  detailHeaderTitle:{ flex:1, fontSize:16, fontWeight:'700', color:'#fff' },
  detailScroll: { flex:1, backgroundColor: COLORS.bg },
  detailMetaCard:{ flexDirection:'row', alignItems:'center', backgroundColor:'#fff', margin:16, padding:16, borderRadius:14, borderWidth:1, borderColor: COLORS.border },
  detailMetaInfo:{ flex:1, marginLeft:12 },
  detailLetterNum:{ fontSize:14, fontWeight:'700', color: COLORS.textDark, marginBottom:6 },
  detailMetaRow:{ flexDirection:'row', alignItems:'center', gap:8, flexWrap:'wrap' },
  statusBadge:  { paddingHorizontal:8, paddingVertical:3, borderRadius:8 },
  statusBadgeText:{ fontSize:10, fontWeight:'700' },

  detailActionBar:{ paddingHorizontal:16, paddingBottom:8, backgroundColor:'#fff', borderBottomWidth:1, borderBottomColor: COLORS.border },
  detailActionBtn:{ flexDirection:'row', alignItems:'center', gap:6, paddingHorizontal:14, paddingVertical:10, marginRight:8, borderRadius:10, backgroundColor:'#f8fafc', borderWidth:1, borderColor: COLORS.border },
  detailActionText:{ fontSize:12, fontWeight:'600', color: COLORS.textMid },

  detailSection:{ backgroundColor:'#fff', margin:12, marginBottom:0, padding:16, borderRadius:14, borderWidth:1, borderColor: COLORS.border },
  detailSectionTitle:{ fontSize:14, fontWeight:'700', color: COLORS.textDark, marginBottom:12 },
  detailSectionRow:{ flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom:12 },
  detailRow:    { flexDirection:'row', justifyContent:'space-between', paddingVertical:6, borderBottomWidth:1, borderBottomColor:'#f1f5f9' },
  detailLabel:  { fontSize:13, color: COLORS.textMid },
  detailValue:  { fontSize:13, fontWeight:'500', color: COLORS.textDark, flex:1, textAlign:'right' },
  detailSubValue:{ fontSize:12, color: COLORS.textMid, marginTop:2 },
  addBtn:       { width:28, height:28, borderRadius:14, backgroundColor:`${COLORS.primary}15`, justifyContent:'center', alignItems:'center' },
  viewAll:      { fontSize:12, fontWeight:'600', color: COLORS.primary },

  // Letter content preview
  letterContentBox:{ backgroundColor:'#fafafa', borderRadius:10, padding:16, borderWidth:1, borderColor: COLORS.border },
  letterheadPreview:{ alignItems:'center', paddingBottom:14, marginBottom:14, borderBottomWidth:2, borderBottomColor: COLORS.primary },
  letterheadName:{ fontSize:18, fontWeight:'700', color: COLORS.primary },
  letterheadDetail:{ fontSize:10, color: COLORS.textMid, textAlign:'center', marginTop:2 },
  letterDate:   { fontSize:12, color: COLORS.textMid, marginBottom:4 },
  letterRef:    { fontSize:12, color: COLORS.textMid, marginBottom:10 },
  letterSalutation:{ fontSize:14, fontWeight:'500', color: COLORS.textDark, marginBottom:10 },
  letterSubjectLine:{ fontSize:14, fontWeight:'700', color: COLORS.textDark, textDecorationLine:'underline', marginBottom:12 },
  letterBody:   { fontSize:13, color: COLORS.textDark, lineHeight:20, marginBottom:16 },
  letterClosing:{ fontSize:14, fontWeight:'500', color: COLORS.textDark, marginBottom:6 },
  letterSignature:{ fontSize:14, fontWeight:'700', color: COLORS.textDark },
  letterSenderTitle:{ fontSize:12, color: COLORS.textMid, marginTop:2 },
  enclosuresBox:{ marginTop:14, paddingTop:10, borderTopWidth:1, borderTopColor: COLORS.border },
  enclosuresLabel:{ fontSize:12, fontWeight:'600', color: COLORS.textDark, marginBottom:4 },
  enclosureItem:{ fontSize:12, color: COLORS.textMid, lineHeight:18 },

  // Reminders
  reminderItem: { flexDirection:'row', alignItems:'flex-start', backgroundColor:'#fffbeb', padding:10, borderRadius:8, marginBottom:8, borderWidth:1, borderColor:'#fde68a' },
  reminderDate: { fontSize:13, fontWeight:'600', color: COLORS.warning },
  reminderNote: { fontSize:12, color: COLORS.textMid, marginTop:2 },

  // Versions
  versionItem:  { flexDirection:'row', alignItems:'flex-start', marginBottom:12 },
  versionBadge: { backgroundColor: COLORS.primary, paddingHorizontal:10, paddingVertical:4, borderRadius:12 },
  versionBadgeText:{ fontSize:11, fontWeight:'700', color:'#fff' },
  versionDate:  { fontSize:12, fontWeight:'600', color: COLORS.textDark },
  versionNote:  { fontSize:12, color: COLORS.textMid },
  versionAuthor:{ fontSize:11, color: COLORS.textLight },
  latestBadge:  { backgroundColor:`${COLORS.success}20`, paddingHorizontal:8, paddingVertical:3, borderRadius:8 },
  latestBadgeText:{ fontSize:10, fontWeight:'600', color: COLORS.success },

  // Comments
  commentItem:  { flexDirection:'row', alignItems:'flex-start', marginBottom:12 },
  commentAvatar:{ width:34, height:34, borderRadius:17, backgroundColor:`${COLORS.primary}20`, justifyContent:'center', alignItems:'center' },
  commentAvatarText:{ fontSize:14, fontWeight:'700', color: COLORS.primary },
  commentHeader:{ flexDirection:'row', justifyContent:'space-between', marginBottom:4 },
  commentAuthor:{ fontSize:13, fontWeight:'600', color: COLORS.textDark },
  commentDate:  { fontSize:11, color: COLORS.textLight },
  commentText:  { fontSize:13, color: COLORS.textMid, lineHeight:18 },

  // Bottom sheets
  bottomSheet:  { flex:1, backgroundColor:'rgba(0,0,0,0.5)', justifyContent:'flex-end' },
  bottomSheetContent:{ backgroundColor:'#fff', borderTopLeftRadius:20, borderTopRightRadius:20, padding:20, maxHeight: height * 0.85 },
  sheetHandle:  { width:40, height:4, borderRadius:2, backgroundColor: COLORS.border, alignSelf:'center', marginBottom:16 },
  sheetHeader:  { flexDirection:'row', alignItems:'center', gap:10, marginBottom:16 },
  sheetTitle:   { flex:1, fontSize:17, fontWeight:'700', color: COLORS.textDark },
  sheetSubtitle:{ fontSize:13, color: COLORS.textMid, marginBottom:12 },
  sheetActions: { flexDirection:'row', gap:12, marginTop:20 },
  resetBtn:     { paddingHorizontal:20, paddingVertical:14, borderRadius:12, borderWidth:1, borderColor: COLORS.border, alignItems:'center' },
  resetBtnText: { fontSize:14, fontWeight:'600', color: COLORS.textMid },

  // Filters
  filterChips:  { flexDirection:'row', flexWrap:'wrap', gap:8 },
  filterChip:   { paddingHorizontal:14, paddingVertical:7, borderRadius:20, backgroundColor:'#f1f5f9', borderWidth:1, borderColor: COLORS.border },
  filterChipActive:{ backgroundColor: COLORS.primary, borderColor: COLORS.primary },
  filterChipText:{ fontSize:12, fontWeight:'500', color: COLORS.textMid },
  filterChipTextActive:{ color:'#fff' },

  // Formatting
  fontChips:    { flexDirection:'row', flexWrap:'wrap', gap:8 },
  fontChip:     { paddingHorizontal:14, paddingVertical:7, borderRadius:10, backgroundColor:'#f1f5f9', borderWidth:1, borderColor: COLORS.border },
  fontChipActive:{ backgroundColor: COLORS.primary, borderColor: COLORS.primary },
  fontChipText: { fontSize:12, color: COLORS.textMid },
  fontChipTextActive:{ color:'#fff' },
  sliderRow:    { flexDirection:'row', gap:8 },
  sizeChip:     { paddingHorizontal:14, paddingVertical:7, borderRadius:10, backgroundColor:'#f1f5f9', borderWidth:1, borderColor: COLORS.border },
  sizeChipActive:{ backgroundColor: COLORS.primary, borderColor: COLORS.primary },
  sizeChipText: { fontSize:12, color: COLORS.textMid },
  marginsGrid:  { flexDirection:'row', flexWrap:'wrap', gap:12 },
  marginItem:   { alignItems:'center' },
  marginLabel:  { fontSize:11, color: COLORS.textMid, marginBottom:4 },

  // Analytics
  kpiGrid:      { flexDirection:'row', flexWrap:'wrap', padding:12, gap:12, justifyContent:'space-between' },
  kpiCard:      { width:(width-52)/2, backgroundColor:'#fff', borderRadius:14, padding:16, alignItems:'center', borderWidth:1, borderColor: COLORS.border },
  kpiValue:     { fontSize:28, fontWeight:'800', marginVertical:6 },
  kpiLabel:     { fontSize:11, color: COLORS.textMid, textAlign:'center' },
  analyticsSection:{ backgroundColor:'#fff', margin:12, padding:16, borderRadius:14, borderWidth:1, borderColor: COLORS.border },
  analyticsSectionTitle:{ fontSize:14, fontWeight:'700', color: COLORS.textDark, marginBottom:14 },
  barRow:       { flexDirection:'row', alignItems:'center', marginBottom:12, gap:10 },
  barLabel:     { flexDirection:'row', alignItems:'center', gap:5, width:110 },
  barLabelText: { fontSize:12, color: COLORS.textMid, flex:1 },
  barTrack:     { flex:1, height:8, backgroundColor:'#f1f5f9', borderRadius:4, overflow:'hidden' },
  barFill:      { height:'100%', borderRadius:4, minWidth:4 },
  barCount:     { width:28, fontSize:12, fontWeight:'600', color: COLORS.textDark, textAlign:'right' },
  activityItem: { flexDirection:'row', alignItems:'center', marginBottom:12 },
  activityDot:  { width:10, height:10, borderRadius:5 },
  activityText: { fontSize:13, color: COLORS.textDark, fontWeight:'500' },
  activityDate: { fontSize:11, color: COLORS.textLight, marginTop:2 },

  // Client picker
  clientItem:   { flexDirection:'row', alignItems:'center', paddingVertical:12, paddingHorizontal:4, borderBottomWidth:1, borderBottomColor:'#f1f5f9' },
  clientAvatar: { width:42, height:42, borderRadius:21, backgroundColor:`${COLORS.primary}20`, justifyContent:'center', alignItems:'center' },
  clientAvatarText:{ fontSize:16, fontWeight:'700', color: COLORS.primary },
  clientName:   { fontSize:14, fontWeight:'600', color: COLORS.textDark },
  clientDetail: { fontSize:12, color: COLORS.textMid, marginTop:2 },
  
});